// db.js - every Supabase REST call lives here. Auth header is the session
// JWT minted by the exchange-code Edge Function; the anon key goes in apikey.
// Postgres RLS policies enforce who can read or write each row - this layer
// is a thin client, not a security boundary.

import { AUTH } from './auth.js';
import { CONFIG } from './config.js';
import { activeClubId } from './clubs.js';
import { canPublish, completenessMessage } from './publish-completeness.js';
import { evaluatorKindOf, scopeOf } from './evaluator-kinds.js';
import { criterionIdFor } from './evaluation-data.js';

export const DB = (() => {

  // ── CORE REQUEST ────────────────────────────────────────────────────────────

  // Make an authenticated request to Supabase REST API.
  async function request(path, opts = {}) {
    const { url, anonKey } = CONFIG.supabase;
    // In server-auth mode, send the session JWT as the bearer token so RLS
    // policies see role_app/team_id/team_code claims. The anonKey stays as
    // the apikey header (Supabase needs it for routing).
    const token = (typeof AUTH !== 'undefined' && AUTH.getToken && AUTH.getToken()) || anonKey;
    const response = await fetch(`${url}/rest/v1/${path}`, {
      headers: {
        'apikey':        anonKey,
        'Authorization': `Bearer ${token}`,
        'Content-Type':  'application/json',
        'Prefer':        opts.prefer || 'return=representation',
        ...(opts.headers || {}),
      },
      ...opts,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`DB error on ${path}: ${error}`);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  }

  /**
   * Every row of a query, fetched a page at a time.
   *
   * PostgREST answers with AT MOST 1000 rows and tells you nothing about the
   * rest, so an unbounded query on a large table quietly returns a slice that
   * looks exactly like a complete answer. That is not hypothetical here: the
   * Dashboard's above-ceiling figure was being computed from 1000 of season
   * 2025-26's 15,798 ratings, which is 6% of the data, and nothing anywhere
   * said so.
   *
   * Anything that can exceed 1000 rows must come through this rather than
   * request(). At ~23 ratings per evaluation, a single season crosses the cap
   * after about 44 evaluations, so "it fits today" is not a reason to skip it.
   *
   * `path` MUST already carry a stable `order=`. PostgREST guarantees no order
   * without one, and unordered paging can return the same row twice and miss
   * another entirely.
   */
  async function requestAll(path, page = 1000) {
    const out = [];
    const sep = path.includes('?') ? '&' : '?';
    for (let from = 0; ; from += page) {
      const rows = await request(`${path}${sep}offset=${from}&limit=${page}`);
      if (!rows || !rows.length) break;
      out.push(...rows);
      if (rows.length < page) break;   // short page means we reached the end
    }
    return out;
  }

  /**
   * `&club_id=eq.<this session's club>`, for list queries that would otherwise
   * ask the database to read every club's rows and let the policies throw
   * nearly all of them away.
   *
   * Belt and braces, not the lock. The row-level policies are what actually
   * stop one club seeing another; this only stops the database doing pointless
   * work, which is the difference between a list that stays fast at 110 clubs
   * and one that gets slower with every club that joins.
   *
   * Only for tables that carry their own club_id.
   */
  const _club = () => `&club_id=eq.${encodeURIComponent(activeClubId())}`;

  /**
   * Attach the evaluation id AND the stable criterion id to rating rows.
   *
   * criterion_id is what a mark is joined to; domain and skill are the names
   * as they stood when it was made. Stamped in one place because there are
   * three write paths and a path that forgets it writes a row that silently
   * drops out of anything grouped by criterion.
   *
   * criterionIdFor() returns null for anything outside the rubric rather than
   * inventing an id, so an unexpected skill name costs one unjoined row
   * instead of failing the foreign key and losing the whole evaluation.
   */
  const _ratingRows = (rows, evaluationId, format) =>
    (rows || []).map(r => ({
      ...r,
      evaluation_id: evaluationId,
      criterion_id: r.criterion_id || criterionIdFor(r.domain, r.skill, format),
    }));

  // ── HEALTH CHECK (director diagnostics screen) ──────────────────────────────
  // Deliberately BYPASSES the session token: probes hit the REST surface the
  // way an anonymous visitor would, so the screen can prove sensitive tables
  // are locked. A probe that comes back OK is a security FAILURE.

  const health = {
    anonProbe: async (table) => {
      const { url, anonKey } = CONFIG.supabase;
      try {
        const res = await fetch(`${url}/rest/v1/${table}?select=*&limit=1`, {
          headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
        });
        return { table, locked: !res.ok, status: res.status };
      } catch {
        return { table, locked: true, status: 0 };
      }
    },

    // Small count helper via PostgREST's exact count (HEAD request).
    count: async (pathWithFilters) => {
      const { url, anonKey } = CONFIG.supabase;
      const token = (AUTH.getToken && AUTH.getToken()) || anonKey;
      const res = await fetch(`${url}/rest/v1/${pathWithFilters}`, {
        method: 'HEAD',
        headers: {
          apikey: anonKey, Authorization: `Bearer ${token}`,
          Prefer: 'count=exact',
        },
      });
      if (!res.ok) throw new Error(`count failed for ${pathWithFilters}: ${res.status}`);
      const range = res.headers.get('content-range') || '';
      const total = Number(range.split('/')[1]);
      return isFinite(total) ? total : null;
    },
  };

  // ── SEASONS ─────────────────────────────────────────────────────────────────

  const seasons = {
    /**
     * The season THIS CLUB is in.
     *
     * Reads the club's own choice, not the global `seasons.is_current` flag.
     * That flag was shared by every club, and the policy guarding it had no
     * club test, so a second club rolling over to a new season rolled every
     * club over with it.
     *
     * Falls back to the global flag for a club that has not chosen one, so a
     * club row added without a season still behaves.
     */
    getCurrent: async () => {
      const club = activeClubId();
      const rows = await request(
        `clubs?id=eq.${encodeURIComponent(club)}&select=current_season_id,seasons!clubs_current_season_id_fkey(*)`
      ).catch(() => []);
      const picked = rows?.[0]?.seasons;
      if (picked) return [picked];
      return request('seasons?is_current=eq.true&select=*');
    },

    getAll: () =>
      request(`seasons?select=*&order=start_date.desc`),

    create: (data) =>
      request('seasons', { method: 'POST', body: JSON.stringify(data) }),

    /**
     * Make `id` the active season FOR THIS CLUB.
     *
     * Writes the club's own row rather than flipping the shared
     * `seasons.is_current` flag, which used to change the season for every
     * club at once. The shared flag is left alone and is now only a default
     * for a club that has never chosen.
     */
    setActive: (id) =>
      request(`clubs?id=eq.${encodeURIComponent(activeClubId())}`, {
        method: 'PATCH',
        prefer: 'return=minimal',
        body: JSON.stringify({ current_season_id: id }),
      }),
  };

  // ── TEAMS ───────────────────────────────────────────────────────────────────

  // The team list is asked for by fifteen modules and changes a few times a
  // season, so it is held per club for the life of the page. Keyed on the club
  // rather than stored in one slot, because wearing another club must not be
  // able to hand back the previous one's teams.
  let _teamsCache = new Map();   // clubId -> Promise<rows>
  const _dropTeamsCache = () => { _teamsCache = new Map(); };

  const teams = {
    // Scoped to the club being worn. Filtering here rather than at the ~30
    // call sites means a second club's teams cannot appear in another club's
    // lists, cohorts or dashboards by anyone forgetting to add a filter.
    //
    // CACHED, and the PROMISE is cached rather than the rows: three screens
    // opening at once used to make three identical requests, and caching the
    // promise means the second and third wait on the first instead. A failed
    // request is evicted so the next caller retries rather than inheriting
    // the error forever.
    getAll: () => {
      const club = activeClubId();
      if (!_teamsCache.has(club)) {
        _teamsCache.set(club, request(
          `teams?select=id,age_group,line,club_id&club_id=eq.${encodeURIComponent(club)}&order=id`
        ).catch(err => { _teamsCache.delete(club); throw err; }));
      }
      return _teamsCache.get(club);
    },

    /** Forget the cached list. Called by every write below. */
    refresh: _dropTeamsCache,

    create: async (data) => {
      const r = await request('teams', { method: 'POST', body: JSON.stringify(data) });
      _dropTeamsCache();
      return r;
    },

    // Update mutable fields (currently just age_group).
    update: async (id, data) => {
      const r = await request(`teams?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH',
        prefer: 'return=minimal',
        body: JSON.stringify(data),
      });
      _dropTeamsCache();
      return r;
    },

    /** Delete a team. Refuses if any memberships still point at it. */
    delete: async (id) => {
      const r = await request(`teams?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE',
        prefer: 'return=minimal',
      });
      _dropTeamsCache();
      return r;
    },
  };

  // ── PLAYERS ─────────────────────────────────────────────────────────────────

  const players = {
    // Active members of a team for a season. Archived players are filtered out
    // here so every caller (rating, code generator, PDFs) gets the same set.
    getByTeam: async (teamId, seasonId) => {
      const rows = await request(
        `memberships?team_id=eq.${teamId}&season_id=eq.${encodeURIComponent(seasonId)}&select=player_id,players(id,name,date_of_birth,archived,is_goalkeeper,twizzit_id)`
      );
      return (rows || []).filter(m => m.players && !m.players.archived);
    },

    // Every player in the DB (active + archived). twizzit_id is needed by the
    // import matcher and the selection bulk-import roster resolver.
    // Club-scoped for the same reason as teams.getAll above: this one feeds
    // the birth-year cohorts behind the Dragons Score, so an unscoped list
    // would quietly put another club's players into Dragons' maths.
    // PAGED. 716 of a 1000 ceiling, and archived players are never removed so
    // it only grows. Its own comment below says it feeds the birth-year cohorts
    // behind the Dragons Score; sorted by name, truncation would quietly drop
    // the S-to-Z children out of their cohort and out of the dashboard.
    getAll: () =>
      requestAll('players?select=id,name,date_of_birth,archived,is_goalkeeper,twizzit_id,club_id' +
                 `&club_id=eq.${encodeURIComponent(activeClubId())}&order=name`),

    // Players by id list - used by the baseline tester (RLS limits the rows it
    // can actually see to the players in its own session).
    getByIds: (ids) =>
      ids && ids.length
        ? request(`players?id=in.(${ids.map(encodeURIComponent).join(',')})&select=id,name`)
        : Promise.resolve([]),

    // Lookup by Twizzit member ID. Returns the row or null.
    findByTwizzitId: async (twizzitId) => {
      if (!twizzitId) return null;
      const rows = await request(
        `players?twizzit_id=eq.${encodeURIComponent(twizzitId)}&select=id,name,date_of_birth,archived,is_goalkeeper,twizzit_id&limit=1`
      );
      return rows?.[0] || null;
    },

    create: (data) =>
      request('players', { method: 'POST', body: JSON.stringify(data) }),

    update: (id, data) =>
      request(`players?id=eq.${id}`, {
        method: 'PATCH',
        prefer: 'return=minimal',
        body: JSON.stringify(data),
      }),

    archive: (id) =>
      request(`players?id=eq.${id}`, {
        method: 'PATCH',
        prefer: 'return=minimal',
        body: JSON.stringify({ archived: true }),
      }),

    unarchive: (id) =>
      request(`players?id=eq.${id}`, {
        method: 'PATCH',
        prefer: 'return=minimal',
        body: JSON.stringify({ archived: false }),
      }),

    // Permanently delete a player and ALL their related data - every evaluation, every rating, every membership a...
    deleteHard: async (id) => {
      // Verify archived first.
      const check = await request(`players?id=eq.${id}&select=archived`);
      if (!check?.length) throw new Error('Player not found');
      if (!check[0].archived) throw new Error('Player must be archived before deletion');

      // Find every evaluation, then delete each (cascades to ratings).
      const evals = await request(`evaluations?player_id=eq.${id}&select=id`);
      for (const ev of (evals || [])) {
        await request(`ratings?evaluation_id=eq.${ev.id}`, { method: 'DELETE', prefer: 'return=minimal' });
        await request(`evaluations?id=eq.${ev.id}`, { method: 'DELETE', prefer: 'return=minimal' });
      }

      // Memberships across all seasons.
      await request(`memberships?player_id=eq.${id}`, { method: 'DELETE', prefer: 'return=minimal' });

      // Finally the player row.
      await request(`players?id=eq.${id}`, { method: 'DELETE', prefer: 'return=minimal' });
    },
  };

  // ── MEMBERSHIPS ─────────────────────────────────────────────────────────────

  const memberships = {
    // Get all memberships for a season (for dashboard overview).
    getAllForSeason: (seasonId) =>
      // Paged as a precaution rather than a cure: 618 rows this season against
      // a 1000 ceiling is 62%, and a club that grows or a season with more
      // movement crosses it without warning.
      requestAll(`memberships?season_id=eq.${encodeURIComponent(seasonId)}`
        + '&select=player_id,team_id&order=player_id'),

    create: (data) =>
      request('memberships', { method: 'POST', body: JSON.stringify(data) }),

    move: (playerId, fromTeamId, toTeamId, seasonId) =>
      request(`memberships?player_id=eq.${playerId}&team_id=eq.${fromTeamId}&season_id=eq.${encodeURIComponent(seasonId)}`, {
        method: 'PATCH',
        prefer: 'return=minimal',
        body: JSON.stringify({ team_id: toTeamId }),
      }),

    // Remove a player from a team for a given season (used when archiving or pulling them out of the squad).
    remove: (playerId, teamId, seasonId) =>
      request(
        `memberships?player_id=eq.${playerId}&team_id=eq.${teamId}&season_id=eq.${encodeURIComponent(seasonId)}`,
        { method: 'DELETE', prefer: 'return=minimal' }
      ),

  };

  // ── TEAM CHANGE LOG (director-only) ─────────────────────────────────────────
  // Within-season team moves, written by the Twizzit re-import sync and the
  // manual move flow. Memberships only hold the CURRENT team per season; this
  // log is the "she moved from G2 to G1 on 12 Nov" memory (director decision
  // 2026-07-02). Best-effort at call sites: a failed log never blocks a move.

  const teamChanges = {
    log: (row) =>
      request('team_change_log', {
        method: 'POST', prefer: 'return=minimal', body: JSON.stringify(row),
      }),

    forPlayer: (playerId) =>
      request(
        'team_change_log?select=season_id,from_team_id,to_team_id,changed_at,source' +
        `&player_id=eq.${encodeURIComponent(playerId)}&order=changed_at`
      ),
  };

  // ── TEAM CODES ──────────────────────────────────────────────────────────────

  const codes = {
    getAll: () =>
      request('team_codes?select=*,teams(id,age_group)&order=team_id'),

    create: (data) =>
      request('team_codes', { method: 'POST', body: JSON.stringify(data) }),

    revoke: (code) => {
      // Set expires_at to YESTERDAY so the Edge Function's `expires_at < today`
      // check rejects the code immediately. Setting it to today would still
      // allow logins for the rest of the day (off-by-one).
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      return request(`team_codes?code=eq.${encodeURIComponent(code)}`, {
        method: 'PATCH',
        prefer: 'return=minimal',
        body: JSON.stringify({ expires_at: yesterday }),
      });
    },
    // Bulk insert - used by the Season Planner to issue every team's code
    // in one round trip.
    createMany: (rows) =>
      request('team_codes', { method: 'POST', body: JSON.stringify(rows) }),
  };

  // ── EVALUATION PLANS ────────────────────────────────────────────────────────

  const evaluationPlans = {
    list: () =>
      request('evaluation_plans?select=*&order=created_at.desc' + _club()),

    create: (data) =>
      request('evaluation_plans', { method: 'POST', body: JSON.stringify(data) }),

    delete: (id) =>
      request(`evaluation_plans?id=eq.${encodeURIComponent(id)}`, { method: 'DELETE', prefer: 'return=minimal' }),

    // No codesForPlan here any more. Plans stopped generating per-window login
    // codes when coaches moved to personal staff codes; the Season Plan screen
    // derives its schedule from the plan row via planWindows() instead.
  };

  // ── EVALUATIONS ─────────────────────────────────────────────────────────────

  const evaluations = {
    // Get all evaluations for a team this season, most recent first.
    getByTeam: (teamId, seasonId) =>
      request(
        // author_staff_id is what identifies a staff coach's own rows; without it
        // AUTH.ownsEvaluation() cannot recognise them (see its comment).
        `evaluations?team_id=eq.${teamId}&season_id=eq.${encodeURIComponent(seasonId)}&select=id,player_id,coach_name,team_code,author_staff_id,evaluated_at&order=evaluated_at.desc`
      ),

    // Get all evaluations for the whole season (for dashboard overview).
    //
    // PAGED. A season is already 693 evaluations and the ceiling is 1000: past
    // it PostgREST returns the first thousand, with no error and nothing to
    // notice. The same thing happened to the Dashboard's above-ceiling figure
    // (see requestAll's comment) and was wrong by a factor of sixteen while
    // looking perfectly normal.
    getAllForSeason: (seasonId) =>
      requestAll(
        `evaluations?season_id=eq.${encodeURIComponent(seasonId)}&select=id,player_id,team_id,coach_name,evaluated_at&order=id`
      ),

    // Get all evaluations for a specific player this season.
    getByPlayer: (playerId, seasonId) =>
      request(
        `evaluations?player_id=eq.${playerId}&season_id=eq.${encodeURIComponent(seasonId)}&select=id,player_id,team_id,coach_name,team_code,evaluated_at,strong_points,working_points,notes&order=evaluated_at.desc`
      ),

    // Get every evaluation for a player across every season.
    getAllByPlayer: (playerId) =>
      request(
        `evaluations?player_id=eq.${playerId}&select=id,player_id,team_id,season_id,age_group,coach_name,team_code,evaluated_at&order=evaluated_at.desc`
      ),

    // Get every coach-issued rating for a season plus enough parent-evaluation context (team_id, coach_name, etc....
    // Paged: a full season is far past PostgREST's 1000-row ceiling (2025-26
    // holds 15,798 of these), and before this it silently returned the first
    // 1000, which made the Dashboard's above-ceiling count wrong by a factor of
    // sixteen without anything appearing broken.
    getAllRatingsForSeason: (seasonId) =>
      requestAll(
        `ratings?select=id,evaluation_id,domain,skill,level,evaluations!inner(team_id,season_id,coach_name,team_code,evaluated_at,player_id)` +
        `&evaluations.season_id=eq.${encodeURIComponent(seasonId)}&order=id`
      ),

    // Get full evaluation detail including ratings.
    getById: async (id) => {
      const [evalData, ratingsData] = await Promise.all([
        request(`evaluations?id=eq.${id}&select=*`),
        request(`ratings?evaluation_id=eq.${id}&select=*`),
      ]);
      return { evaluation: evalData?.[0], ratings: ratingsData };
    },

    // Every evaluation records WHO made it and AT WHAT RESOLUTION. Derived here
    // rather than at the four call sites, because a row that reaches the
    // database without a scope is a row that can quietly put a fast pass's
    // invented per-skill detail into the workbook.
    create: async (evalData, ratingsData) => {
      const stamped = {
        ...evalData,
        evaluator_kind: evalData.evaluator_kind || evaluatorKindOf(evalData),
        rating_scope:   evalData.rating_scope   || scopeOf(evalData),
      };
      const result = await request('evaluations', {
        method: 'POST',
        body: JSON.stringify(stamped),
      });
      const evalId = result[0].id;
      if (ratingsData && ratingsData.length) {
        await request('ratings', {
          method: 'POST',
          body: JSON.stringify(_ratingRows(ratingsData, evalId, stamped.eval_format)),
        });
      }
      return evalId;
    },

    // Delete an evaluation and all its ratings.
    delete: async (evalId) => {
      // Delete ratings first
      await request(`ratings?evaluation_id=eq.${evalId}`, {
        method: 'DELETE',
        prefer: 'return=minimal',
      });
      // Then delete the evaluation
      await request(`evaluations?id=eq.${evalId}`, {
        method: 'DELETE',
        prefer: 'return=minimal',
      });
    },

    update: async (evalId, evalData, ratingsData) => {
      await request(`evaluations?id=eq.${evalId}`, {
        method: 'PATCH',
        prefer: 'return=minimal',
        body: JSON.stringify(evalData),
      });
      // Delete old ratings and replace
      await request(`ratings?evaluation_id=eq.${evalId}`, {
        method: 'DELETE',
        prefer: 'return=minimal',
      });
      if (ratingsData && ratingsData.length) {
        await request('ratings', {
          method: 'POST',
          body: JSON.stringify(_ratingRows(ratingsData, evalId, evalData?.eval_format)),
        });
      }
    },
  };

  // ── TEAM SCORE CEILINGS ─────────────────────────────────────────────────────
  //
  // Director sets a max rating level per (team, season, domain). Optional
  // per-skill overrides also live here - distinguished by `skill IS NULL`
  // vs `skill = <name>`. The enforcement trigger lives in the database; this
  // namespace is just the CRUD layer for the calibration UI and the lookup.

  const ceilings = {
    /** All ceilings for the active season - used by the calibration list. */
    listForSeason: (seasonId) =>
      request(`team_score_ceilings?season_id=eq.${encodeURIComponent(seasonId)}&select=*`),

    /** All ceilings for one team in one season - used by the calibration screen. */
    listForTeam: (teamId, seasonId) =>
      request(
        `team_score_ceilings?team_id=eq.${encodeURIComponent(teamId)}` +
        `&season_id=eq.${encodeURIComponent(seasonId)}&select=*`
      ),

    // Upsert a ceiling row.
    set: async (teamId, seasonId, domain, skill, maxLevel, setBy) => {
      // Two-phase: delete any existing matching row first, then insert.
      // Cleaner than fighting Postgres's ON CONFLICT semantics with a partial
      // unique index over a nullable column.
      await ceilings.clear(teamId, seasonId, domain, skill);
      return request('team_score_ceilings', {
        method: 'POST',
        prefer: 'return=minimal',
        body: JSON.stringify({
          team_id:   teamId,
          season_id: seasonId,
          domain,
          skill:     skill || null,
          max_level: maxLevel,
          set_by:    setBy || null,
        }),
      });
    },

    /** Remove a ceiling row (sets the affected level back to "no cap"). */
    clear: (teamId, seasonId, domain, skill) => {
      let q = `team_score_ceilings?team_id=eq.${encodeURIComponent(teamId)}` +
              `&season_id=eq.${encodeURIComponent(seasonId)}` +
              `&domain=eq.${encodeURIComponent(domain)}`;
      q += skill ? `&skill=eq.${encodeURIComponent(skill)}` : `&skill=is.null`;
      return request(q, { method: 'DELETE', prefer: 'return=minimal' });
    },

    // Copy every ceiling row for a team from one season to another.
    copySeason: async (teamId, fromSeasonId, toSeasonId) => {
      const rows = await ceilings.listForTeam(teamId, fromSeasonId);
      if (!rows?.length) return [];
      // Wipe target season's existing rows for this team first.
      await request(
        `team_score_ceilings?team_id=eq.${encodeURIComponent(teamId)}` +
        `&season_id=eq.${encodeURIComponent(toSeasonId)}`,
        { method: 'DELETE', prefer: 'return=minimal' }
      );
      const payload = rows.map(r => ({
        team_id:   teamId,
        season_id: toSeasonId,
        domain:    r.domain,
        skill:     r.skill,
        max_level: r.max_level,
      }));
      return request('team_score_ceilings', {
        method: 'POST',
        prefer: 'return=minimal',
        body: JSON.stringify(payload),
      });
    },
  };

  // ── SELECTION TRAININGS ──────────────────────────────────────────────────────

  const selection = {
    // -- Trainings ------------------------------------------------------------
    // Active = not archived. Default UI lists hide archived items.
    listTrainings: () =>
      request('selection_trainings?select=*&deleted_at=is.null&order=date.desc' + _club()),

    // Archived list - for the "Show archived" view that lets the director restore items or permanently delete them.
    listArchivedTrainings: () =>
      request('selection_trainings?select=*&deleted_at=not.is.null&order=deleted_at.desc' + _club()),

    getTraining: (id) =>
      request(`selection_trainings?id=eq.${encodeURIComponent(id)}&select=*`)
        .then(r => r?.[0] || null),

    createTraining: (data) =>
      request('selection_trainings', {
        method: 'POST',
        body: JSON.stringify(data),
      }).then(r => r?.[0] || null),

    // Director toggles whether assigned trainers can see + mark a session.
    setActive: (id, active) =>
      request(`selection_trainings?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ is_active: !!active }),
      }),

    // Replace the disabled-sections list for one training, or for many at once (bulk path).
    setDisabledSections: (id, sections) =>
      request(`selection_trainings?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ disabled_sections: sections || [] }),
      }),

    setDisabledSectionsBulk: (ids, sections) =>
      request(
        `selection_trainings?id=in.(${ids.map(encodeURIComponent).join(',')})`,
        {
          method: 'PATCH', prefer: 'return=minimal',
          body: JSON.stringify({ disabled_sections: sections || [] }),
        },
      ),

    // Soft delete - hide from default lists.
    archiveTraining: (id) =>
      request(`selection_trainings?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ deleted_at: new Date().toISOString() }),
      }),

    restoreTraining: (id) =>
      request(`selection_trainings?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ deleted_at: null }),
      }),

    /** Bulk archive - same as archiveTraining but for many ids. */
    archiveTrainings: (ids) =>
      request(
        `selection_trainings?id=in.(${ids.map(encodeURIComponent).join(',')})`,
        {
          method: 'PATCH', prefer: 'return=minimal',
          body: JSON.stringify({ deleted_at: new Date().toISOString() }),
        }
      ),

    // TRUE delete - cascades and wipes everything attached (bibs, marks, votes, Bronco).
    permanentlyDeleteTraining: (id) =>
      request(`selection_trainings?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    permanentlyDeleteTrainings: (ids) =>
      request(
        `selection_trainings?id=in.(${ids.map(encodeURIComponent).join(',')})`,
        { method: 'DELETE', prefer: 'return=minimal' }
      ),

    // -- Trainers (one persistent code per trainer) --------------------------
    listTrainers: () =>
      request('selection_trainers?select=*&deleted_at=is.null&order=name' + _club()),

    listArchivedTrainers: () =>
      request('selection_trainers?select=*&deleted_at=not.is.null&order=deleted_at.desc' + _club()),

    createTrainer: (data) =>
      request('selection_trainers', {
        method: 'POST',
        body: JSON.stringify(data),
      }).then(r => r?.[0] || null),

    updateTrainer: (id, data) =>
      request(`selection_trainers?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify(data),
      }),

    // Soft delete - hide from active lists, keep their historical marks / comments / team_votes attributed.
    archiveTrainer: (id) =>
      request(`selection_trainers?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ deleted_at: new Date().toISOString() }),
      }),

    restoreTrainer: (id) =>
      request(`selection_trainers?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ deleted_at: null }),
      }),

    // TRUE delete - selection_marks.trainer_id is now SET NULL on cascade so the engagement data survives anonymo....
    permanentlyDeleteTrainer: (id) =>
      request(`selection_trainers?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),


    // Sanitised trainer list (id, name, role - NO codes) for one training.
    trainersForTraining: (trainingId) =>
      request('rpc/selection_trainers_for_training', {
        method: 'POST',
        body: JSON.stringify({ p_training: trainingId }),
      }),

    // -- Assignments (which trainer covers which session) --------------------
    listAssignments: () =>
      request('selection_assignments?select=*'),

    createAssignments: (rows) =>
      request('selection_assignments', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify(rows),
      }),

    deleteAssignmentsForTrainer: (trainerId) =>
      request(`selection_assignments?trainer_id=eq.${encodeURIComponent(trainerId)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    deleteAssignmentsForTrainerTrainings: (trainerId, trainingIds) => {
      if (!trainingIds || !trainingIds.length) return Promise.resolve();
      const ids = trainingIds.map(encodeURIComponent).join(',');
      return request(
        `selection_assignments?trainer_id=eq.${encodeURIComponent(trainerId)}&training_id=in.(${ids})`,
        { method: 'DELETE', prefer: 'return=minimal' }
      );
    },

    // -- Bibs -----------------------------------------------------------------
    listBibs: (trainingId) =>
      request(
        `selection_bibs?training_id=eq.${encodeURIComponent(trainingId)}` +
        `&deleted_at=is.null&select=*&order=bib_color,bib_number`
      ),

    listArchivedBibs: (trainingId) =>
      request(
        `selection_bibs?training_id=eq.${encodeURIComponent(trainingId)}` +
        `&deleted_at=not.is.null&select=*&order=deleted_at.desc`
      ),

    createBibs: (rows) =>
      request('selection_bibs', { method: 'POST', body: JSON.stringify(rows) }),

    updateBib: (id, data) =>
      request(`selection_bibs?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify(data),
      }),

    // Soft delete - hide the bib from the active grid + report but preserve every mark, comment, and vote tied to it.
    archiveBib: (id) =>
      request(`selection_bibs?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ deleted_at: new Date().toISOString() }),
      }),

    restoreBib: (id) =>
      request(`selection_bibs?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ deleted_at: null }),
      }),

    /** TRUE delete - cascades the bib's marks/comments/votes. */
    permanentlyDeleteBib: (id) =>
      request(`selection_bibs?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),


    // Mark a bib absent (a no-show) or present again.
    setBibAbsent: (id, absent) =>
      request('rpc/set_selection_bib_absent', {
        method: 'POST',
        prefer: 'return=minimal',
        body: JSON.stringify({ p_bib_id: id, p_value: !!absent }),
      }),

    deleteAllBibs: (trainingId) =>
      request(`selection_bibs?training_id=eq.${encodeURIComponent(trainingId)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    // Flag a bib for "more eyes" (any selection coach in the training) or clear the flag (director only).
    flagBib: (bibId, flagged) =>
      request('rpc/flag_selection_bib', {
        method: 'POST',
        prefer: 'return=minimal',
        body: JSON.stringify({ p_bib_id: bibId, p_value: !!flagged }),
      }),

    // -- Marks ----------------------------------------------------------------
    // Explicit column list (not `select=*`) so the 8-second director-report
    // poll doesn't drag every column over the wire. Only the four fields the
    // aggregator + grid actually use.
    // PAGED. A busy selection day is trainers x bibs x categories: the biggest
    // so far is 544 taps. Past 1000 the EARLIEST taps of the session would be
    // dropped and every net would simply come out lower, with nothing to see.
    listMarks: (trainingId) =>
      requestAll(
        `selection_marks?training_id=eq.${encodeURIComponent(trainingId)}` +
        `&select=id,bib_id,trainer_id,category,delta&order=created_at.desc`
      ),

    listMyMarks: (trainingId) =>
      // RLS already filters to the current trainer_id; we just hit the table.
      // NO limit - the previous limit=200 silently dropped older marks once a
      // trainer crossed ~200 taps, which made bib ticks vanish from the grid
      // after the client re-fetched on navigation.
      request(
        `selection_marks?training_id=eq.${encodeURIComponent(trainingId)}` +
        `&select=id,bib_id,trainer_id,category,delta&order=created_at.desc`
      ),

    // Bulk insert from the coach UI's tap-batching layer; instant feedback
    // even under fast tapping.
    createMarks: (rows) =>
      request('selection_marks', { method: 'POST', body: JSON.stringify(rows) })
        .then(r => r || []),

    deleteMark: (id) =>
      request(`selection_marks?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    // -- Comments (per category per trainer per bib) -------------------------
    listComments: (trainingId) =>
      request(`selection_comments?training_id=eq.${encodeURIComponent(trainingId)}&select=*`),

    upsertComment: (data) =>
      request('selection_comments?on_conflict=training_id,bib_id,trainer_id,category', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({ ...data, updated_at: new Date().toISOString() }),
      }),

    deleteComment: (trainingId, bibId, trainerId, category) =>
      request(
        `selection_comments?training_id=eq.${encodeURIComponent(trainingId)}` +
        `&bib_id=eq.${encodeURIComponent(bibId)}` +
        `&trainer_id=eq.${encodeURIComponent(trainerId)}` +
        `&category=eq.${encodeURIComponent(category)}`,
        { method: 'DELETE', prefer: 'return=minimal' }
      ),

    // -- Team votes (latest wins) --------------------------------------------
    listVotes: (trainingId) =>
      request(`selection_team_votes?training_id=eq.${encodeURIComponent(trainingId)}&select=*`),

    upsertVote: (data) =>
      request('selection_team_votes?on_conflict=training_id,bib_id,trainer_id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({ ...data, updated_at: new Date().toISOString() }),
      }),

    // -- Session-position votes (top / mid / bottom of today's group) --------
    // Distinct from team_votes (which team) - this captures the panel's
    // collective view of where a player sits overall in the session.
    listSessionTiers: (trainingId) =>
      request(`selection_session_tier_votes?training_id=eq.${encodeURIComponent(trainingId)}&select=*`),

    upsertSessionTier: (data) =>
      request('selection_session_tier_votes?on_conflict=training_id,bib_id,trainer_id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({ ...data, updated_at: new Date().toISOString() }),
      }),

    deleteSessionTier: (trainingId, bibId, trainerId) =>
      request(
        `selection_session_tier_votes` +
        `?training_id=eq.${encodeURIComponent(trainingId)}` +
        `&bib_id=eq.${encodeURIComponent(bibId)}` +
        `&trainer_id=eq.${encodeURIComponent(trainerId)}`,
        { method: 'DELETE', prefer: 'return=minimal' },
      ),

    // -- Progress (counts per bib for head-trainer view) ---------------------
    listProgress: (trainingId) =>
      request(`selection_progress?training_id=eq.${encodeURIComponent(trainingId)}&select=*`),

    // -- Bronco test (fitness-trainer timed run) -----------------------------
    // The run row holds the start/stop epoch; times are one finish per bib.
    bronco: {
      getRun: (trainingId) =>
        request(`selection_bronco_runs?training_id=eq.${encodeURIComponent(trainingId)}&select=*`)
          .then(r => r?.[0] || null),

      /** START - upsert the run row with a fresh start time, clearing any stop. */
      startRun: (data) =>
        request('selection_bronco_runs?on_conflict=training_id', {
          method: 'POST',
          prefer: 'resolution=merge-duplicates,return=minimal',
          body: JSON.stringify({ ...data, stopped_at: null }),
        }),

      /** STOP - freeze the run by stamping stopped_at. */
      stopRun: (trainingId, stoppedAt) =>
        request(`selection_bronco_runs?training_id=eq.${encodeURIComponent(trainingId)}`, {
          method: 'PATCH', prefer: 'return=minimal',
          body: JSON.stringify({ stopped_at: stoppedAt }),
        }),

      /** RESET - drop the run row (finish times are cleared via clearTimes). */
      resetRun: (trainingId) =>
        request(`selection_bronco_runs?training_id=eq.${encodeURIComponent(trainingId)}`, {
          method: 'DELETE', prefer: 'return=minimal',
        }),

      listTimes: (trainingId) =>
        request(`selection_bronco_times?training_id=eq.${encodeURIComponent(trainingId)}&select=*`),

      /** Record (or overwrite) one bib's finish time. */
      recordTime: (data) =>
        request('selection_bronco_times?on_conflict=training_id,bib_id', {
          method: 'POST',
          prefer: 'resolution=merge-duplicates,return=minimal',
          body: JSON.stringify({ ...data, recorded_at: new Date().toISOString() }),
        }),

      deleteTime: (trainingId, bibId) =>
        request(
          `selection_bronco_times?training_id=eq.${encodeURIComponent(trainingId)}` +
          `&bib_id=eq.${encodeURIComponent(bibId)}`,
          { method: 'DELETE', prefer: 'return=minimal' }
        ),

      clearTimes: (trainingId) =>
        request(`selection_bronco_times?training_id=eq.${encodeURIComponent(trainingId)}`, {
          method: 'DELETE', prefer: 'return=minimal',
        }),
    },
  };

  // ── STAFF (canonical coach identity - Phase 0c Fix 3, director-only) ─────────
  // One `staff` row per real person; `staff_aliases` maps every spelling of
  // their name (lower/trimmed) to that row. Module 6 (Coach Hub) builds on
  // this same table.

  const staff = {
    // Active staff with their aliases embedded.
    list: () =>
      request('staff?select=*,staff_aliases(alias_norm)&archived_at=is.null&order=full_name' + _club()),

    // Manually add a person (new coaches who have no historical data yet).
    // The alias row makes future free-text coach_name spellings link to them.
    //
    // The alias is a convenience, not part of the person. alias_norm is the
    // primary key of staff_aliases, so a second person with an existing
    // spelling collides, and letting that collision escape used to abandon a
    // staff row that had already been committed: the caller saw an error, the
    // person appeared in the roster anyway, and every retry left another one.
    // So the alias is best-effort, and the staff row is what we return.
    create: async (fullName) => {
      const rows = await request('staff', {
        method: 'POST',
        body: JSON.stringify({ full_name: fullName }),
      });
      const created = rows?.[0];
      if (created?.id) {
        try {
          await request('staff_aliases?on_conflict=alias_norm', {
            method: 'POST',
            // ignore-duplicates: the spelling keeps pointing at whoever claimed
            // it first. Two real people can share a name, and guessing which one
            // an old free-text entry meant is worse than leaving it as it was.
            prefer: 'resolution=ignore-duplicates,return=minimal',
            body: JSON.stringify({ alias_norm: fullName.trim().toLowerCase(), staff_id: created.id }),
          });
        } catch (err) {
          console.warn('Alias not linked for', fullName, err);
        }
      }
      return created;
    },

    rename: (id, fullName) =>
      request(`staff?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ full_name: fullName }),
      }),

    /** Head trainers and directors: evaluate any pillar, any time. */
    setUnrestricted: (id, on) =>
      request(`staff?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ unrestricted_eval: !!on }),
      }),

    /**
     * May read the published season plan for their age band(s). Off for
     * everyone by default: the club holds the plans closer than the rest of the
     * library, so access is granted by name rather than by role.
     */
    setSeesPlans: (id, on) =>
      request(`staff?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ season_plan_access: !!on }),
      }),

    /**
     * The DTA badge worn in this coach's hub header. Null clears it, which is a
     * real state rather than a downgrade: the header goes back to how it looked
     * before they were given one.
     */
    setDtaBadge: (id, key) =>
      request(`staff?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ dta_badge: key || null }),
      }),

    /**
     * Head trainers: read every evaluation in the age groups already ticked as
     * their trainer ages. Reading only, and only those ages, so an Onderbouw
     * head trainer never sees the Bovenbouw. Writing is unchanged.
     */
    setOverseesAges: (id, on) =>
      request(`staff?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ oversees_ages: !!on }),
      }),

    // Head trainers who may author staff evaluations (Module 6, L9).
    setEvaluator: (id, isEvaluator) =>
      request(`staff?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ is_evaluator: !!isEvaluator }),
      }),

    // Trainers see the DTA Library's trainer-only section (drills/exercises).
    // `trainerAges` scopes which age groups' trainer content they see (empty =
    // all ages). Passing isTrainer=false clears the ages too.
    setTrainer: (id, isTrainer, trainerAges = []) =>
      request(`staff?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({
          is_trainer: !!isTrainer,
          trainer_ages: isTrainer ? (trainerAges || []) : [],
        }),
      }),

    // Set both halves of the role at once (Coach / Trainer / Coach-Trainer), so
    // the roster's Role dialog is a single write rather than two.
    setRole: (id, { isCoach, isTrainer, trainerAges = [] }) =>
      request(`staff?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({
          is_coach:     !!isCoach,
          is_trainer:   !!isTrainer,
          trainer_ages: isTrainer ? (trainerAges || []) : [],
        }),
      }),

    archive: (id) =>
      request(`staff?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ archived_at: new Date().toISOString() }),
      }),

    // Merge: every alias of `fromId` re-points to `intoId`, then the emptied
    // record is archived. History (evaluations, trainer records) resolves
    // through aliases, so it follows automatically.
    merge: async (fromId, intoId) => {
      await request(`staff_aliases?staff_id=eq.${encodeURIComponent(fromId)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ staff_id: intoId }),
      });
      await staff.archive(fromId);
    },

    // Distinct evaluation coach names - used to count evaluations per staff
    // record client-side (joined through the alias map).
    // PAGED. Every evaluation ever, to count them per coach: 697 today and
    // roughly +700 a season, so it crosses the ceiling next season and each
    // coach's count would start quietly under-reporting.
    evalCoachNames: () =>
      requestAll('evaluations?select=coach_name&order=id'),
  };

  // ── RUBRIC (the matrices) ───────────────────────────────────────────────────
  //
  // The standard wording ships in src/evaluation-data.js and is what every
  // club gets. This reads only the rows a club has CHANGED, which for every
  // club today is none, so the request returns an empty array and costs
  // nothing. See the header of 20261029190000_rubric_descriptor_overrides.sql
  // for why it is sparse rather than a full copy.

  const rubric = {
    /**
     * The criterion list, for anything that needs ids rather than labels.
     * Shared rows (club_id NULL) plus this club's own, if it ever forks.
     */
    criteria: () =>
      request('rubric_criteria?select=criterion_id,eval_format,domain,skill,position' +
              '&retired_at=is.null&order=eval_format,domain,position'),

    /**
     * This club's descriptor overrides. Scoped by RLS to the caller's own
     * club, so no club id is sent: asking for someone else's would return
     * nothing anyway, and not sending one means a bug cannot request it.
     */
    /**
     * This club's descriptor overrides, EVERY version of each.
     *
     * Not just the current wording, because a descriptor is the definition of
     * a level: an evaluation from last May has to read the words that were in
     * force last May, or a family is shown a definition their daughter's coach
     * never used. The windows are resolved client-side by
     * getDescriptors(..., asOf).
     *
     * Scoped by RLS to the caller's own club, so no club id is sent: asking
     * for someone else's would return nothing anyway, and not sending one
     * means a bug cannot request it.
     */
    overridesFor: () =>
      request('rubric_descriptors' +
              '?select=criterion_id,age_band,level,lang,descriptor,valid_from,superseded_at' +
              '&order=valid_from'),

    /** Rewrite one level's wording. Closes the old row and opens a new one. */
    setDescriptor: (criterionId, ageBand, level, lang, descriptor) =>
      request('rpc/set_rubric_descriptor', {
        method: 'POST',
        body: JSON.stringify({
          p_criterion_id: criterionId, p_age_band: ageBand || '',
          p_level: level, p_lang: lang || 'en', p_descriptor: descriptor,
        }),
      }),

    /** Drop an override, so the standard wording applies again from now on. */
    clearDescriptor: (criterionId, ageBand, level, lang) =>
      request('rpc/clear_rubric_descriptor', {
        method: 'POST',
        body: JSON.stringify({
          p_criterion_id: criterionId, p_age_band: ageBand || '',
          p_level: level, p_lang: lang || 'en',
        }),
      }),
  };

  // ── GRADING PASSES (the band belongs to the pass, not to the team) ──────────
  //
  // A band is step one of an evaluation pass, set against the rubric
  // descriptors by whoever is running it, and it does not outlive that pass.
  // There is deliberately NO "latest band for this team" reader here: nothing
  // carries forward, so a new pass starts from the full scale and the
  // evaluator does the work again. That is what stops a band being inherited,
  // going stale, or being drafted from the marks it produced.
  //
  // Replaced `coordinatorBands`, whose name had stopped describing what it
  // held: the standing band table and its drafting readers are gone, and what
  // is left is all about one pass. Director decision 29 October 2026.

  const gradingPasses = {
    /**
     * Open a pass and record what the evaluator marked.
     *
     * Two writes rather than one, because the band rows need the pass id. If
     * the second fails the pass is removed again: a pass with no band would
     * resolve every tap against nothing, and leaving one behind would put a
     * phantom occasion into the team's history.
     *
     * club_id is left out of the body on purpose; the column default fills it
     * from the caller's own token, which is what stops one club writing a pass
     * into another's.
     */
    create: async (pass, bands) => {
      const rows = (bands || []).filter(b =>
        b && b.domain && b.skill
        && Number.isInteger(b.floor_level) && Number.isInteger(b.ceiling_level));
      if (!rows.length) throw new Error('A pass cannot be opened without a band.');

      const created = await request('grading_passes?select=id', {
        method: 'POST',
        prefer: 'return=representation',
        body: JSON.stringify({
          team_id:         pass.team_id,
          season_id:       pass.season_id,
          eval_format:     pass.eval_format || 'field_v1',
          evaluator_kind:  pass.evaluator_kind,
          set_by:          pass.set_by,
          author_staff_id: pass.author_staff_id || null,
        }),
      });
      const passId = created?.[0]?.id;
      if (!passId) throw new Error('Pass was not created.');

      try {
        await request('grading_pass_bands', {
          method: 'POST',
          prefer: 'return=minimal',
          body: JSON.stringify(rows.map(b => ({
            pass_id: passId,
            domain: b.domain, skill: b.skill,
            floor_level: b.floor_level, ceiling_level: b.ceiling_level,
          }))),
        });
      } catch (err) {
        await request(`grading_passes?id=eq.${encodeURIComponent(passId)}`,
          { method: 'DELETE', prefer: 'return=minimal' }).catch(() => {});
        throw err;
      }
      return passId;
    },

    /** The band this pass was graded against. */
    bandsFor: (passId) =>
      request('grading_pass_bands?select=domain,skill,floor_level,ceiling_level' +
              `&pass_id=eq.${encodeURIComponent(passId)}&order=domain,skill`),

    /**
     * Every pass on a team this season, newest first, with its band.
     *
     * This IS the record of the team developing: a band moving from 2 to 4 up
     * to 3 to 5 over a season is the measurement, and it only exists because
     * nothing overwrites anything. Paged, because a club banding five pillars
     * a fortnight across thirty teams passes a thousand rows inside a season.
     */
    history: (teamId, seasonId) =>
      requestAll('grading_passes?select=id,team_id,season_id,eval_format,' +
                 'evaluator_kind,set_by,author_staff_id,created_at,' +
                 'grading_pass_bands(domain,skill,floor_level,ceiling_level)' +
                 `&team_id=eq.${encodeURIComponent(teamId)}` +
                 `&season_id=eq.${encodeURIComponent(seasonId)}` +
                 _club() + '&order=created_at.desc'),

    /**
     * Write one pillar of a pass: a whole squad's placements, in one go.
     *
     * AN EVALUATION ACCUMULATES. One row per player per season, found or
     * created, and each pass replaces only the pillar it covers. Somebody who
     * does Technical in October and Mental in January leaves one evaluation
     * carrying two finished pillars, which is exactly what the publish rule
     * wants: whole pillars, and the family sees the ones that are done.
     *
     * So it finds-and-updates rather than deleting and recreating, which would
     * throw away October's work on the way to saving January's.
     *
     * `passId` is how an evaluation records the scale it was resolved against.
     * Without it, a level that moved between two passes could not be read
     * apart into the player changing and the team changing.
     *
     * @param {Array}  entries    [{ player, team, ratings: [{domain, skill, level}] }]
     * @param {string} domain     the one pillar being written
     * @param {string} coachName  who to show the family
     * @param {string} passId     the pass whose band produced these placements
     * @param {string} authorCode DIRECTOR | CODIRECTOR | COORDINATOR, which sets the weight
     */
    savePillarPass: async (entries, domain, coachName, passId = null, authorCode = null) => {
      if (!entries.length) return 0;
      const season = CONFIG.season.current;
      // The author decides the weight: a director's pass counts 1.5x, anyone
      // else's once. Defaults to the coordinator, who the pass was built for
      // before the director started running the same one.
      const code = authorCode || CONFIG.access.coordinatorCode;
      const ids = entries.map(e => e.player.id);
      const existing = await requestAll(
        'evaluations?select=id,player_id' +
        `&season_id=eq.${encodeURIComponent(season)}` +
        `&team_code=eq.${encodeURIComponent(code)}` +
        `&player_id=in.(${ids.map(encodeURIComponent).join(',')})&order=id`);
      const byPlayer = new Map((existing || []).map(e => [e.player_id, e.id]));

      let written = 0;
      for (const entry of entries) {
        const evalId = byPlayer.get(entry.player.id);
        const rows = entry.ratings.map(r => ({ ...r, comment: null }));
        if (evalId) {
          // Replace this pillar only. The player may also have moved team
          // since the last pass, so the header is refreshed with it.
          await request(`ratings?evaluation_id=eq.${evalId}` +
                        `&domain=eq.${encodeURIComponent(domain)}`,
                        { method: 'DELETE', prefer: 'return=minimal' });
          await request('ratings', {
            method: 'POST', prefer: 'return=minimal',
            body: JSON.stringify(_ratingRows(rows, evalId, 'field_v1')),
          });
          await request(`evaluations?id=eq.${evalId}`, {
            method: 'PATCH', prefer: 'return=minimal',
            body: JSON.stringify({
              team_id: entry.team.id, age_group: entry.team.age_group,
              coach_name: coachName, evaluated_at: new Date().toISOString(),
              // This pillar was just re-placed against THIS pass's band, so
              // the row now belongs to this pass, at pillar resolution.
              ...(passId ? { pass_id: passId, rating_scope: 'pillar' } : {}),
            }),
          });
        } else {
          await evaluations.create({
            player_id: entry.player.id,
            team_id:   entry.team.id,
            season_id: season,
            age_group: entry.team.age_group,
            coach_name: coachName,
            team_code: code,
            author_staff_id: null,
            eval_format: 'field_v1',
            evaluated_at: new Date().toISOString(),
            pass_id: passId,
            // Every row this writes is a PLACEMENT against the pass's band,
            // never a per-criterion observation, whoever signed the pass.
            // Stated here as well as derived in scopeOf() because this is the
            // only place placements are written and it must not depend on who
            // happens to be holding the tablet.
            rating_scope: 'pillar',
          }, rows);
        }
        written += 1;
      }
      return written;
    },

    /** A coordinator's own marks for a team this season, to show what is done. */

    /** A coordinator's own marks for a team this season, to show what is done. */
    passSoFar: (teamId, seasonId) =>
      requestAll(
        'ratings?select=domain,skill,level,evaluations!inner(player_id,team_id,season_id,team_code)' +
        `&evaluations.team_id=eq.${encodeURIComponent(teamId)}` +
        `&evaluations.season_id=eq.${encodeURIComponent(seasonId)}` +
        `&evaluations.team_code=eq.${encodeURIComponent(CONFIG.access.coordinatorCode)}` +
        '&order=id'),

    /**
     * Every mark these players already carry this season, from anyone but the
     * coordinator, so the panel can show where a child stood before the tap.
     */

    /**
     * Every mark these players already carry this season, from anyone but the
     * coordinator, so the panel can show where a child stood before the tap.
     */
    standingForPlayers: (playerIds, seasonId) => {
      const ids = (playerIds || []).filter(Boolean);
      if (!ids.length) return Promise.resolve([]);
      return requestAll(
        'ratings?select=domain,level,evaluations!inner(player_id,season_id,team_code)' +
        `&level=gt.0&evaluations.season_id=eq.${encodeURIComponent(seasonId)}` +
        `&evaluations.team_code=neq.${encodeURIComponent(CONFIG.access.coordinatorCode)}` +
        `&evaluations.player_id=in.(${ids.map(encodeURIComponent).join(',')})&order=id`);
    },

    /** The director's ceilings for a team, which outrank any band. */

    /** The director's ceilings for a team, which outrank any band. */
    ceilingsForTeam: (teamId, seasonId) =>
      request('team_score_ceilings?select=domain,skill,max_level' +
              `&team_id=eq.${encodeURIComponent(teamId)}` +
              `&season_id=eq.${encodeURIComponent(seasonId)}`),
  };

  // ── PLAYER SEASON SUMMARY (Phase 0c view - Modules 3/4/5 foundation) ─────────

  const playerSeasonSummary = {
    // PAGED. This is the cohort spine: every percentile in the Dragons Score is
    // computed against these rows. 618 for the current season against a 1000
    // ceiling, and truncation here does not just lose players, it changes the
    // percentile of every player who REMAINS. Its sibling below was paged from
    // the start, with a comment about this very ceiling; this one was missed
    // thirteen lines away.
    list: (seasonId) =>
      requestAll(
        'player_season_summary?select=*' +
        (seasonId ? `&season_id=eq.${encodeURIComponent(seasonId)}` : '') +
        '&order=player_name'
      ),

    // Per-skill means for the Master Analysis workbook. The view reconciles the
    // two rubric generations (old lowercase domains + older skill labels) onto
    // the current matrix names, so one skill is one column. Director-gated in
    // the view itself. Well past the 1000-row ceiling (~25 skills x hundreds of
    // players), hence requestAll; this was the first query to hit it and used to
    // carry its own copy of the paging loop.
    skills: (seasonId) =>
      requestAll(
        'player_skill_summary?select=player_id,pillar,skill,mean_level,weighted_level,n_ratings' +
        (seasonId ? `&season_id=eq.${encodeURIComponent(seasonId)}` : '') +
        '&order=player_id'
      ),
  };

  // ── PLAYER PAGE, player side (Module 1 - RLS scopes everything to the
  //    logged-in player's own published rows) ──────────────────────────────────

  const playerPage = {
    // Published evaluations for the logged-in player. RLS does the filtering;
    // an empty list simply means nothing is published. The season filter is
    // the visibility switch: at rollover, last season goes dark by itself.
    myEvaluations: () =>
      request(
        'evaluations?select=*&order=evaluated_at.desc' +
        (CONFIG.player.currentSeasonOnly
          ? `&season_id=eq.${encodeURIComponent(CONFIG.season.current)}` : '')
      ),

    /**
     * The rating rows for this player's own evaluations.
     *
     * NAME THE EVALUATIONS. Row-level security already limits what comes back
     * to her own published and shared ones, so asking for the whole table was
     * safe, but it made the database read every rating in the club and throw
     * nearly all of them away: 15,816 rows examined to return 25, measured at
     * 949ms for one girl opening her page. The caller already holds her
     * evaluations, so it can simply say which.
     *
     * With no ids there is nothing of hers to fetch, so skip the round trip.
     *
     * @param {string[]} evalIds  her own evaluation ids
     */
    myRatings: (evalIds) => {
      const ids = (evalIds || []).filter(Boolean);
      if (!ids.length) return Promise.resolve([]);
      return request(
        'ratings?select=evaluation_id,domain,skill,level,comment' +
        `&evaluation_id=in.(${ids.map(encodeURIComponent).join(',')})`
      );
    },

    // Stored translations of the player's visible feedback (RLS: own + visible).
    myEvaluationTranslations: () =>
      request('evaluation_translations?select=evaluation_id,field,target_lang,translated_text,source_text'),

    myAcknowledgements: () =>
      request('evaluation_acknowledgements?select=*'),

    // -- Baseline (player side; RLS returns only own published rows) -----------
    myBaselineSessions: () =>
      request('baseline_sessions?select=*&order=session_date.desc' + _club()),

    myBaselineResults: () =>
      request('baseline_results?select=session_id,exercise_key,value,value_push,value_shot,recorded_at'),

    myBaselineAcks: () =>
      request('baseline_acknowledgements?select=*'),

    // -- Player self-testing (Parkrun model, P3). RLS lets a player see member
    //    sessions, read their exercises, and insert their own source='self' row.
    // Self-testing is a per-session opt-in (director decision 2026-07-02):
    // only open sessions the director flagged appear on "Test yourself".
    myOpenBaselineSessions: () =>
      request('baseline_sessions?status=eq.open&self_test_enabled=eq.true&select=*&order=session_date.desc'),

    baselineSessionExercises: (sessionId) =>
      request(`baseline_session_exercises?session_id=eq.${encodeURIComponent(sessionId)}&select=exercise_key`),

    // -- Certificate photo (young ages). Private 'player-photos' bucket; RLS lets
    //    a player read/write only their own "{player_id}/photo". `pid` is passed
    //    in (equals jwt_player) so db.js needs no AUTH dependency.
    uploadPhoto: (pid, file) =>
      _storageRequest(`${pid}/photo`, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'image/jpeg', 'x-upsert': 'true' },
        body: file,
      }, 'player-photos'),

    photoObjectUrl: async (pid) => {
      try {
        const r = await _storageRequest(`${pid}/photo`, { method: 'GET' }, 'player-photos');
        return URL.createObjectURL(await r.blob());
      } catch { return null; } // 404 = no photo uploaded yet
    },

    recordSelfResult: (payload) =>
      request('baseline_results', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify({ ...payload, source: 'self' }),
      }),

    acknowledgeBaseline: (playerId, sessionId) =>
      request('baseline_acknowledgements?on_conflict=player_id,session_id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({ player_id: playerId, session_id: sessionId }),
      }),

    acknowledge: (playerId, evaluationId) =>
      request('evaluation_acknowledgements?on_conflict=player_id,evaluation_id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({ player_id: playerId, evaluation_id: evaluationId }),
      }),

    // -- Post-match self-reflection (player side; RLS scopes to own team/rows) --
    // Reflections the coach has opened for the player's team (RLS returns only
    // their own team's rows). The player fills one per request.
    myReflectionRequests: () =>
      request('self_eval_requests?select=*&order=match_date.desc,created_at.desc'),

    // The player's own reflections, each with the coach's review embedded so
    // feedback shows without a second call.
    myReflections: () =>
      request('self_evals?select=*,self_eval_reviews(*)&order=submitted_at.desc'),

    // Insert or edit the player's reflection for one request. submitted_at is
    // left off the payload so it keeps the first-submit time across edits.
    submitReflection: (payload) =>
      request('self_evals?on_conflict=request_id,player_id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=representation',
        body: JSON.stringify({ ...payload, updated_at: new Date().toISOString() }),
      }),

    // The player confirms they have seen the coach's feedback.
    markReflectionSeen: (selfEvalId) =>
      request(`self_evals?id=eq.${encodeURIComponent(selfEvalId)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ review_seen_at: new Date().toISOString() }),
      }),
  };

  // ── PLAYER ACCESS, director side (Module 1) ──────────────────────────────────

  /**
   * Refuse to publish anything with a half-finished pillar.
   *
   * Reads the ratings of the evaluations about to go out, together with
   * whether each player is a goalkeeper (their rubric is a different set of
   * skills, so "complete" means something else for them), and throws with a
   * message naming the pillar and how far short it fell.
   *
   * Throwing rather than returning false is deliberate: every caller already
   * sits inside a try/catch that shows the message, so a new publish path
   * cannot forget to check. It has to go out of its way to ignore it.
   */
  async function _assertPublishable(evaluationIds) {
    const ids = (evaluationIds || []).filter(Boolean);
    if (!ids.length) return;
    const list = ids.map(encodeURIComponent).join(',');

    const [evals, ratings] = await Promise.all([
      request(`evaluations?id=in.(${list})&select=id,player_id,rating_scope,evaluator_kind,team_code,players(is_goalkeeper)`),
      requestAll(`ratings?evaluation_id=in.(${list})&select=evaluation_id,domain,skill,level&order=id`),
    ]);

    const byEval = new Map();
    (ratings || []).forEach(r => {
      if (!byEval.has(r.evaluation_id)) byEval.set(r.evaluation_id, []);
      byEval.get(r.evaluation_id).push(r);
    });

    const problems = [];
    (evals || []).forEach(ev => {
      // A fast pass is a calibration instrument, not a report. Its pillar mark
      // is one tap, and the criterion rows behind it are six copies of that
      // tap: a family reading them would see detail that was never observed.
      // It counts towards the pillar everywhere internally and goes nowhere.
      if (scopeOf(ev) === 'pillar') {
        problems.push('A fast evaluation cannot be shared: it is a pillar-level judgement, not a full evaluation.');
        return;
      }
      const format = ev.players?.is_goalkeeper ? 'gk_v1' : 'field_v1';
      const verdict = canPublish(byEval.get(ev.id) || [], format);
      if (!verdict.ok) problems.push(completenessMessage(verdict));
    });

    if (problems.length) {
      const err = new Error(problems.length === 1
        ? problems[0]
        : `${problems.length} evaluations are not ready. ${problems[0]}`);
      err.code = 'incomplete_evaluation';
      throw err;
    }
  }

  const playerAccess = {
    listRequests: () =>
      request('player_access_requests?select=*&order=created_at.desc' + _club()),

    approve: (id, playerId) =>
      request(`player_access_requests?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({
          status: 'approved', player_id: playerId,
          decided_at: new Date().toISOString(),
        }),
      }),

    reject: (id) =>
      request(`player_access_requests?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ status: 'rejected', decided_at: new Date().toISOString() }),
      }),

    revoke: (id) =>
      request(`player_access_requests?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ status: 'revoked', decided_at: new Date().toISOString() }),
      }),

    // Evaluation publish state for a set of players (director view).
    evaluationsForPlayers: (playerIds) =>
      playerIds.length
        ? request(
            'evaluations?select=id,player_id,team_code,coach_name,evaluated_at,published_to_player_at,shared_individually_at' +
            `&player_id=in.(${playerIds.map(encodeURIComponent).join(',')})` +
            '&order=evaluated_at.desc'
          )
        : Promise.resolve([]),

    // All not-yet-published evaluations of a season - feeds the
    // publish-by-block flow (grouped into fortnights client-side).
    unpublishedForSeason: (seasonId) =>
      request(
        'evaluations?select=id,player_id,team_id,team_code,coach_name,evaluated_at,published_to_player_at' +
        `&season_id=eq.${encodeURIComponent(seasonId)}&published_to_player_at=is.null` +
        '&order=evaluated_at.desc'
      ),

    // Every evaluation of a season, published or not, for the sharing
    // dashboard. unpublishedForSeason above cannot serve it: the dashboard has
    // to show what has ALREADY gone out as well as what is waiting, and it
    // needs the nominations to say how many are ready for the top/bottom view.
    allForSeason: (seasonId) =>
      request(
        'evaluations?select=id,player_id,team_id,team_code,coach_name,evaluated_at,' +
        'published_to_player_at,nominated_strength,nominated_working' +
        `&season_id=eq.${encodeURIComponent(seasonId)}&order=evaluated_at.desc`
      ),

    /**
     * Share one evaluation with its family, or take it back.
     *
     * A HALF PILLAR NEVER GOES OUT (director, 28 Oct 2026). The check lives
     * here rather than on a screen because four different screens publish, and
     * a rule that only one of them enforces is not a rule. Unpublishing is
     * never blocked: taking something back must always be possible.
     */
    setPublished: async (evaluationId, published) => {
      if (published) await _assertPublishable([evaluationId]);
      return request(`evaluations?id=eq.${encodeURIComponent(evaluationId)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({
          published_to_player_at: published ? new Date().toISOString() : null,
        }),
      });
    },

    // Director exception to the aggregate-only view: show this ONE evaluation
    // to its player as a separate, fully visible document.
    // Sharing one evaluation as its own document is still the family seeing it,
    // so the same rule applies. Withdrawing is never blocked.
    setSharedIndividually: async (evaluationId, shared) => {
      if (shared) await _assertPublishable([evaluationId]);
      return request(`evaluations?id=eq.${encodeURIComponent(evaluationId)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({
          shared_individually_at: shared ? new Date().toISOString() : null,
        }),
      });
    },

    // Same rule in bulk, and ALL OR NOTHING on purpose: publishing a team and
    // quietly skipping the two unfinished ones would leave the director
    // believing the whole team had gone out.
    publishMany: async (evaluationIds) => {
      if (!evaluationIds.length) return Promise.resolve();
      await _assertPublishable(evaluationIds);
      return request(`evaluations?id=in.(${evaluationIds.map(encodeURIComponent).join(',')})`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ published_to_player_at: new Date().toISOString() }),
      });
    },

    listAcknowledgements: () =>
      request('evaluation_acknowledgements?select=*'),
  };

  // ── COACH HUB (Module 6): staff codes, evaluations, feedback notes ───────────
  // Director + head-trainer evaluators write; the staff member's own writes
  // (Part D, acknowledge, note-seen) go through narrow SECURITY DEFINER RPCs.

  // ── STAFF SELF-APPLY, director side ──────────────────────────────────────────
  // Coaches/trainers apply via the staff-auth Edge Function; the director
  // reviews here. Approval is orchestrated in the UI: staff.create -> issue the
  // request's code (staffHub.createCode) -> set role/ages/team -> approve().
  const staffAccess = {
    listRequests: () =>
      request('staff_access_requests?select=*&order=created_at.desc' + _club()),

    approve: (id, staffId) =>
      request(`staff_access_requests?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({
          status: 'approved', staff_id: staffId,
          decided_at: new Date().toISOString(),
        }),
      }),

    reject: (id) =>
      request(`staff_access_requests?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ status: 'rejected', decided_at: new Date().toISOString() }),
      }),

    revoke: (id) =>
      request(`staff_access_requests?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ status: 'revoked', decided_at: new Date().toISOString() }),
      }),
  };

  const staffHub = {
    // -- Personal staff codes (director-only via RLS) --------------------------
    listCodes: () =>
      request('staff_codes?select=*&order=created_at.desc'),

    createCode: (staffId, code) =>
      request('staff_codes', {
        method: 'POST',
        body: JSON.stringify({ staff_id: staffId, code }),
      }),

    revokeCode: (id) =>
      request(`staff_codes?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ revoked_at: new Date().toISOString() }),
      }),

    // -- Evaluations (evaluator side; RLS scopes head trainers to own-authored) -
    listEvaluations: () =>
      request('staff_evaluations?select=*&order=created_at.desc'),

    createEvaluation: (data) =>
      request('staff_evaluations', { method: 'POST', body: JSON.stringify(data) }),

    updateEvaluation: (id, data) =>
      request(`staff_evaluations?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify(data),
      }),

    deleteEvaluation: (id) =>
      request(`staff_evaluations?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    /**
     * Ask the chosen people for a self-evaluation this season.
     *
     * The reflection lives ON the evaluation row, so asking either flags the
     * open evaluation this person already has, or opens a fresh one to hold
     * their answers until the director writes the rest. Re-asking someone who
     * has already been asked just refreshes the date rather than opening a
     * second review of the same person in the same season.
     */
    requestSelfEval: async (staffIds, seasonId) => {
      const ids = [...new Set((staffIds || []).filter(Boolean))];
      if (!ids.length) return { asked: 0, opened: 0 };
      const open = await request('staff_evaluations?select=id,staff_id'
        + `&season_id=eq.${encodeURIComponent(seasonId)}&locked_at=is.null`);
      const existing = new Map((open || []).map(r => [r.staff_id, r.id]));
      const at = new Date().toISOString();

      const fresh = ids.filter(id => !existing.has(id));
      for (const id of ids.filter(x => existing.has(x))) {
        await request(`staff_evaluations?id=eq.${encodeURIComponent(existing.get(id))}`, {
          method: 'PATCH', prefer: 'return=minimal',
          body: JSON.stringify({ reflection_requested_at: at }),
        });
      }
      if (fresh.length) {
        await request('staff_evaluations', {
          method: 'POST', prefer: 'return=minimal',
          body: JSON.stringify(fresh.map(staff_id => ({
            staff_id, season_id: seasonId, reflection_requested_at: at,
          }))),
        });
      }
      return { asked: ids.length, opened: fresh.length };
    },

    /** Stop asking someone (the request is withdrawn, their answers are kept). */
    cancelSelfEval: (evaluationId) =>
      request(`staff_evaluations?id=eq.${encodeURIComponent(evaluationId)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ reflection_requested_at: null }),
      }),

    getRatings: (evaluationId) =>
      request(`staff_evaluation_ratings?evaluation_id=eq.${encodeURIComponent(evaluationId)}&select=*`),

    // Upsert one criterion's rating/comment (rating null = N/A).
    saveRating: (evaluationId, criterionKey, rating, comment) =>
      request('staff_evaluation_ratings?on_conflict=evaluation_id,criterion_key', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({
          evaluation_id: evaluationId, criterion_key: criterionKey,
          rating, comment: comment || null,
        }),
      }),

    deleteRating: (evaluationId, criterionKey) =>
      request(
        `staff_evaluation_ratings?evaluation_id=eq.${encodeURIComponent(evaluationId)}` +
        `&criterion_key=eq.${encodeURIComponent(criterionKey)}`,
        { method: 'DELETE', prefer: 'return=minimal' }
      ),

    getActions: (evaluationId) =>
      request(`staff_evaluation_actions?evaluation_id=eq.${encodeURIComponent(evaluationId)}&select=*&order=position`),

    addAction: (evaluationId, body, position) =>
      request('staff_evaluation_actions', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify({ evaluation_id: evaluationId, body, position: position || 0 }),
      }),

    setActionStatus: (id, status) =>
      request(`staff_evaluation_actions?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ status }),
      }),

    deleteAction: (id) =>
      request(`staff_evaluation_actions?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    // -- Staff self-service (RPCs + published-only reads) ----------------------
    // Only self-evaluations the director actually asked for, this season.
    myPendingReflections: () =>
      request('rpc/my_pending_reflections', {
        method: 'POST',
        body: JSON.stringify({ p_season: CONFIG.season.current }),
      }),

    saveReflection: (evalId, well, improve, support) =>
      request('rpc/save_staff_reflection', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify({ p_eval: evalId, p_well: well, p_improve: improve, p_support: support }),
      }),

    myPublishedEvaluations: () =>
      request('staff_evaluations?select=*&order=evaluated_on.desc'),

    // ── Head trainers: the age groups they oversee ────────────────────────────
    // No age filter in any of these on purpose. RLS decides what comes back: a
    // staff member with oversees_ages sees only the ages ticked on them, and one
    // without it sees only their own work. Filtering client-side as well would
    // mean two copies of the rule that can quietly drift apart.

    // Deliberately a thin projection and NOT capped. The head trainer's real
    // question is "who has been missed", which needs every row to answer
    // honestly; a capped list would under-count evaluations and invent gaps
    // that are not there. Five small columns over a few hundred rows is
    // cheaper than one wrong answer. Bodies are fetched per evaluation on open.
    // PAGED. Both of these feed "who has not been evaluated yet", where losing
    // rows reads as reassurance rather than as a problem.
    ageGroupEvaluations: () =>
      requestAll('evaluations?select=id,player_id,team_id,coach_name,evaluated_at'
        + `&season_id=eq.${encodeURIComponent(CONFIG.season.current)}`
        + '&order=evaluated_at.desc'),

    // The full roster for those ages, which is what makes "not yet evaluated"
    // answerable. Without it we could only ever count who HAS been seen.
    // PAGED, and it had no order at all, so which rows came back was undefined
    // and could differ between loads. 637 of 1000 today.
    ageGroupMemberships: () =>
      requestAll(`memberships?season_id=eq.${encodeURIComponent(CONFIG.season.current)}`
        + '&select=player_id,team_id&order=player_id'),

    // Names. Also RLS-scoped, so a head trainer resolves only children inside
    // their own age bands.
    ageGroupPlayers: () =>
      request('players?select=id,name&order=name'),

    // One evaluation in full, on open rather than up front.
    evaluationDetail: (evalId) =>
      request(`evaluations?id=eq.${encodeURIComponent(evalId)}`
        + '&select=id,player_id,team_id,coach_name,evaluated_at,strong_points,working_points,notes'),

    ratingsForEvaluation: (evalId) =>
      request(`ratings?evaluation_id=eq.${encodeURIComponent(evalId)}`
        + '&select=domain,skill,level,comment'),

    acknowledgeEvaluation: (evalId) =>
      request('rpc/acknowledge_staff_evaluation', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify({ p_eval: evalId }),
      }),

    // -- Feedback notes ---------------------------------------------------------
    listNotesFor: (staffId) =>
      request(`staff_feedback_notes?staff_id=eq.${encodeURIComponent(staffId)}&select=*&order=created_at.desc`),

    createNote: (data) =>
      request('staff_feedback_notes', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify(data),
      }),

    deleteNote: (id) =>
      request(`staff_feedback_notes?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    myNotes: () =>
      request('staff_feedback_notes?select=*&order=created_at.desc'),

    markNoteSeen: (id) =>
      request('rpc/mark_staff_note_seen', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify({ p_note: id }),
      }),

    // Notes since a date - the "since last review" panel beside Part E.
    notesSince: (staffId, sinceIso) =>
      request(
        `staff_feedback_notes?staff_id=eq.${encodeURIComponent(staffId)}` +
        (sinceIso ? `&created_at=gte.${encodeURIComponent(sinceIso)}` : '') +
        '&select=*&order=created_at.desc'
      ),

    // -- Team assignments (which team(s) a coach evaluates + the plan) ----------
    listAssignments: () =>
      request('staff_team_assignments?select=*'),

    assignTeam: (staffId, teamId, planId) =>
      request('staff_team_assignments?on_conflict=staff_id,team_id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({ staff_id: staffId, team_id: teamId, plan_id: planId || null }),
      }),

    unassignTeam: (staffId, teamId) =>
      request(
        `staff_team_assignments?staff_id=eq.${encodeURIComponent(staffId)}` +
        `&team_id=eq.${encodeURIComponent(teamId)}`,
        { method: 'DELETE', prefer: 'return=minimal' }
      ),
  };

  // ── DTA LIBRARY (Module 6): resources + view tracking + Storage ──────────────

  // First Supabase Storage use: the private 'coach-resources' bucket. Same
  // auth model as PostgREST - session JWT as bearer, storage RLS decides.
  const STORAGE_BUCKET = 'coach-resources';

  async function _storageRequest(path, opts = {}, bucket = STORAGE_BUCKET) {
    const { url, anonKey } = CONFIG.supabase;
    const token = (typeof AUTH !== 'undefined' && AUTH.getToken && AUTH.getToken()) || anonKey;
    // Spread opts FIRST, then set headers, so a caller's `headers` (e.g. an
    // upload's Content-Type) merges with - never overwrites - the auth headers.
    const { headers: optHeaders, ...rest } = opts;
    const response = await fetch(`${url}/storage/v1/object/${bucket}/${path}`, {
      ...rest,
      headers: {
        'apikey':        anonKey,
        'Authorization': `Bearer ${token}`,
        ...(optHeaders || {}),
      },
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Storage error on ${path}: ${error}`);
    }
    return response;
  }

  const dta = {
    // Director sees everything; staff RLS filters to published rows.
    listResources: () =>
      request('coach_resources?select=*&order=pinned.desc,published_at.desc.nullslast,created_at.desc' + _club()),

    createResource: (data) =>
      request('coach_resources', { method: 'POST', body: JSON.stringify(data) }),

    updateResource: (id, data) =>
      request(`coach_resources?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify(data),
      }),

    deleteResource: (id) =>
      request(`coach_resources?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    listViews: () =>
      request('coach_resource_views?select=*'),

    // Staff: record (or refresh) my view of a resource.
    recordView: (resourceId, staffId) =>
      request('coach_resource_views?on_conflict=resource_id,staff_id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({
          resource_id: resourceId, staff_id: staffId,
          viewed_at: new Date().toISOString(),
        }),
      }),

    // -- Storage (PDFs + article images) -----------------------------------------
    // Upload any file; the Content-Type comes from the file itself (the bucket
    // allows application/pdf + image/png,jpeg,webp - see migration ...0006/...0006a).
    uploadFile: async (path, file) => {
      await _storageRequest(path, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
        body: file,
      });
      return path;
    },

    uploadPdf: async (path, file) => {
      await _storageRequest(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/pdf', 'x-upsert': 'true' },
        body: file,
      });
      return path;
    },

    downloadPdf: async (path) => {
      const r = await _storageRequest(path, { method: 'GET' });
      return await r.blob();
    },

    // Same fetch, returned as an object URL ready for an <img> src.
    imageUrl: async (path) => {
      const r = await _storageRequest(path, { method: 'GET' });
      return URL.createObjectURL(await r.blob());
    },

    deletePdf: (path) =>
      _storageRequest(path, { method: 'DELETE' }),
  };

  // ── SEASON PLAN PRACTICES (Module 6) ────────────────────────────────────────
  // The club's six season plans as data. Director sees everything; staff RLS
  // filters to published rows. Diagrams live in their own table and are fetched
  // one practice at a time - the list must never drag ~13KB of SVG per row
  // across a phone connection.

  // Everything the list and the practice screen need EXCEPT the diagrams.
  const PRACTICE_COLS = [
    'id', 'ref', 'band', 'band_label', 'code', 'season_week', 'block_key',
    'block_index', 'order_index', 'type_label', 'title', 'title_nl',
    'block_label', 'block_label_nl', 'principle', 'principle_nl', 'area',
    'players', 'duration', 'kit', 'kit_nl', 'organisation', 'organisation_nl',
    'points', 'points_nl', 'cues', 'cues_nl', 'good', 'good_nl', 'questions',
    'questions_nl', 'detail', 'detail_nl', 'progression', 'progression_nl',
    'adapt_easier', 'adapt_harder', 'adapt_easier_nl', 'adapt_harder_nl',
    'dragons_link', 'minutes', 'players_min', 'players_max', 'format', 'space',
    'intensity', 'tags', 'skills', 'pillars', 'diagram_caption2',
    'published_at',
  ].join(',');

  // ── COACH-WRITTEN EXERCISES (Module 6) ──────────────────────────────────────
  // The first table staff write to. RLS does the deciding: a staff member reads
  // their own rows plus everyone's published-and-not-hidden ones, and can only
  // ever write as themselves. Nothing here re-checks that; it would only be a
  // second, weaker copy of the rule.

  const exercises = {
    // Everything this session is allowed to see, in one call. For a coach that
    // is their own drafts plus the club's published ones; for the director it
    // is the lot, taken-down ones included.
    list: () =>
      request('coach_exercises?select=*&order=created_at.desc' + _club()),

    create: (data) =>
      request('coach_exercises', { method: 'POST', body: JSON.stringify(data) }),

    update: (id, data) =>
      request(`coach_exercises?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify(data),
      }),

    /**
     * The author withdraws their exercise. A soft delete on purpose: it goes
     * for every coach including them, but the club keeps the row.
     *
     * Through an RPC rather than a PATCH, because the staff read policy hides
     * deleted rows and Postgres will not let an update make a row invisible to
     * itself. Staff have no DELETE policy on this table either, so there is no
     * hard-delete path at all.
     */
    remove: (id) =>
      request('rpc/withdraw_exercise', {
        method: 'POST', body: JSON.stringify({ p_id: id }),
      }),

    /** The author decides when their own work goes live. */
    setPublished: (id, publishedAt) =>
      request(`coach_exercises?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ published_at: publishedAt }),
      }),

    /**
     * Director only: take something down, or put it back. With no approval gate
     * in front of publishing, this is the club's only brake, so it is a plain
     * timestamp rather than a delete and nothing is destroyed by using it.
     */
    setHidden: (id, hiddenAt) =>
      request(`coach_exercises?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ hidden_at: hiddenAt }),
      }),
  };

  const practices = {
    // Only the coach's own age band(s). Pulling all 492 rows would be six times
    // the payload for content they would never open. A coach with two teams in
    // different ages gets both.
    listByBands: (bands) => {
      const list = (bands || []).filter(Boolean);
      if (!list.length) return Promise.resolve([]);
      return request(`coach_practices?select=${PRACTICE_COLS}`
        + `&band=in.(${list.map(b => encodeURIComponent(b)).join(',')})`
        + '&order=season_week.asc.nullslast,block_index.asc,order_index.asc');
    },

    // Director view: every band, for the admin screen and the publish pass.
    listAll: () =>
      request(`coach_practices?select=${PRACTICE_COLS}`
        + '&order=band.asc,season_week.asc.nullslast,block_index.asc,order_index.asc'),

    diagrams: (ref) =>
      request('coach_practice_diagrams?select=n,svg'
        + `&practice_ref=eq.${encodeURIComponent(ref)}&order=n.asc`),

    // Director: publish / unpublish, one band at a time or the whole plan.
    setPublished: (refs, publishedAt) =>
      request(`coach_practices?ref=in.(${refs.map(r => `"${r}"`).join(',')})`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ published_at: publishedAt }),
      }),

    publishBand: (band, publishedAt) =>
      request(`coach_practices?band=eq.${encodeURIComponent(band)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ published_at: publishedAt }),
      }),
  };

  // ── BASELINE TESTING (Module 2) ──────────────────────────────────────────────
  // Director CRUD on sessions; tester reads its own session + writes results
  // (RLS scopes everything by the JWT's baseline_session_id).

  const baseline = {
    // -- Director: sessions ----------------------------------------------------
    listSessions: () =>
      request('baseline_sessions?select=*&order=session_date.desc,created_at.desc' + _club()),

    getSession: async (id) =>
      (await request(`baseline_sessions?id=eq.${encodeURIComponent(id)}&select=*`))?.[0] || null,

    createSession: async (data) =>
      (await request('baseline_sessions', { method: 'POST', body: JSON.stringify(data) }))?.[0] || null,

    updateSession: (id, data) =>
      request(`baseline_sessions?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal', body: JSON.stringify(data),
      }),

    deleteSession: (id) =>
      request(`baseline_sessions?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    // -- Session exercises + players (set membership) --------------------------
    listExercises: (sessionId) =>
      request(`baseline_session_exercises?session_id=eq.${encodeURIComponent(sessionId)}&select=exercise_key,record_mode`),

    // `keys` is either plain exercise keys (every one recorded per player, the
    // old shape) or { key, mode } rows, because the recording mode is chosen
    // per test so one session can mix them (director 2026-10-30).
    setExercises: async (sessionId, keys) => {
      await request(`baseline_session_exercises?session_id=eq.${encodeURIComponent(sessionId)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      });
      if (keys.length) {
        await request('baseline_session_exercises', {
          method: 'POST', prefer: 'return=minimal',
          body: JSON.stringify(keys.map(k => ({
            session_id: sessionId,
            exercise_key: typeof k === 'string' ? k : k.key,
            record_mode: (typeof k === 'string' ? null : k.mode) || 'player',
          }))),
        });
      }
    },

    listPlayers: (sessionId) =>
      request(`baseline_session_players?session_id=eq.${encodeURIComponent(sessionId)}&select=player_id`),

    setPlayers: async (sessionId, playerIds) => {
      await request(`baseline_session_players?session_id=eq.${encodeURIComponent(sessionId)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      });
      const ids = [...new Set(playerIds)];
      const CHUNK = 50;
      for (let i = 0; i < ids.length; i += CHUNK) {
        await request('baseline_session_players', {
          method: 'POST', prefer: 'return=minimal',
          body: JSON.stringify(ids.slice(i, i + CHUNK).map(pid => ({ session_id: sessionId, player_id: pid }))),
        });
      }
    },

    // -- External / guest players in a session (Physical Testing P1) ----------
    listExternals: (sessionId) =>
      request(`baseline_session_externals?session_id=eq.${encodeURIComponent(sessionId)}&select=*&order=name`),

    addExternal: (sessionId, name) =>
      request('baseline_session_externals', {
        method: 'POST', prefer: 'return=representation',
        body: JSON.stringify({ session_id: sessionId, name }),
      }),

    // Replace the session's guest list (used by the create wizard).
    setExternals: async (sessionId, names) => {
      await request(`baseline_session_externals?session_id=eq.${encodeURIComponent(sessionId)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      });
      const clean = [...new Set((names || []).map(n => (n || '').trim()).filter(Boolean))];
      if (clean.length) {
        await request('baseline_session_externals', {
          method: 'POST', prefer: 'return=minimal',
          body: JSON.stringify(clean.map(name => ({ session_id: sessionId, name }))),
        });
      }
    },

    // -- Tester codes ----------------------------------------------------------
    listTesterCodes: (sessionId) =>
      request(`baseline_tester_codes?session_id=eq.${encodeURIComponent(sessionId)}&select=*&order=created_at`),

    // The OPEN sessions a coach may run for one of their OWN teams, with no
    // tester code at all: either they opened it, or it belongs to a team they
    // are assigned to (director 2026-10-30, "any coach can record for their
    // own team or open a test for their own team"). RLS does the deciding;
    // bl_sessions_staff_own_read returns nothing else.
    myTeamSessions: () =>
      request(
        'baseline_sessions?select=*&status=eq.open' +
        '&team_id=not.is.null&order=session_date.desc,created_at.desc'
      ),

    // Open a session for one of my own teams. created_by_staff_id and team_id
    // are not decoration: together they ARE the coach's permission to run it,
    // and the insert policy refuses the row without them.
    createTeamSession: async (data) =>
      (await request('baseline_sessions', { method: 'POST', body: JSON.stringify(data) }))?.[0] || null,

    // The OPEN sessions a logged-in staff coach is assigned to test (their own
    // ST- login; RLS returns only their own tester_code rows). Each carries the
    // tester_code_id the coach writes results under.
    myTesterSessions: () =>
      request(
        'baseline_tester_codes?select=id,session_id,baseline_sessions!inner(id,name,session_date,status)' +
        '&baseline_sessions.status=eq.open&order=created_at.desc'
      ),

    createTesterCode: (sessionId, code, testerName, staffId) =>
      request('baseline_tester_codes', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify({ session_id: sessionId, code, tester_name: testerName, staff_id: staffId || null }),
      }),

    revokeTesterCode: (id) =>
      request(`baseline_tester_codes?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ revoked_at: new Date().toISOString() }),
      }),

    // -- Results (director read-all; tester scoped by RLS) ---------------------
    listResults: (sessionId) =>
      request(`baseline_results?session_id=eq.${encodeURIComponent(sessionId)}&select=*`),

    recordResult: (data) =>
      request('baseline_results', {
        method: 'POST', prefer: 'return=minimal', body: JSON.stringify(data),
      }),

    deleteResult: (id) =>
      request(`baseline_results?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),
  };

  // ── PHYSICAL TESTS LIBRARY (Physical Testing P1) ────────────────────────────
  // Custom/extended test definitions. Readable by any signed-in role (so the
  // tester/results/player screens resolve names); director writes.

  const physicalTests = {
    list: () =>
      request('physical_tests?select=*&order=sort,name'),

    listActive: () =>
      request('physical_tests?select=*&active=eq.true&order=sort,name'),

    create: (row) =>
      request('physical_tests', {
        method: 'POST', prefer: 'return=representation', body: JSON.stringify(row),
      }),

    update: (key, patch) =>
      request(`physical_tests?key=eq.${encodeURIComponent(key)}`, {
        method: 'PATCH', prefer: 'return=minimal', body: JSON.stringify(patch),
      }),

    deactivate: (key) =>
      request(`physical_tests?key=eq.${encodeURIComponent(key)}`, {
        method: 'PATCH', prefer: 'return=minimal', body: JSON.stringify({ active: false }),
      }),
  };

  // ── RANKING (Module 3 - Dragons Score, director-only) ───────────────────────

  const ranking = {
    // Evaluations + their ratings for a season - feeds the bias-corrected
    // coach axis (director reads all via RLS).
    evaluationsWithRatings: (seasonId) =>
      // PAGED, and this is the one that matters most. It feeds the Dragons
      // Score. At 693 evaluations a season it is 69% of the 1000-row ceiling,
      // and the season a club crosses it the score would be computed from the
      // first thousand evaluations and look entirely normal while being wrong.
      // A silently wrong number is worse than a missing one.
      requestAll(
        // `domain` rides along because the bias correction z-scores each coach
        // per pillar, not pooled: see coachZByPlayer in dragons-score.js.
        'evaluations?select=id,player_id,team_code,author_staff_id,ratings(level,domain)' +
        `&season_id=eq.${encodeURIComponent(seasonId)}&order=id`
      ),

    // Baseline results across the season's sessions - feeds the measured axis.
    // PAGED. 271 rows today so it is not wrong yet, but it feeds the measured
    // axis of the Dragons Score and grows with every testing session.
    /**
     * Test results that may reach the Dragons Score.
     *
     * SELF-RECORDED RESULTS ARE EXCLUDED. A player can enter their own time in
     * an open self-test session (policy bl_results_player_self_insert, which
     * is correctly scoped: their own row, their own session, tagged
     * source='self'). Nothing reviewed it, so it must not score. Without this
     * filter the first self-recorded time a child entered would land in the
     * measured axis immediately, and nothing would look wrong.
     *
     * The DIRECTOR accepting one is what makes it count (only the director has
     * UPDATE on baseline_results, so only the director can set accepted_at).
     * That is the whole point of the review screen: not a formality on top of
     * the data, the thing that turns it into data. So the test is "not
     * self-recorded, OR accepted", and an unreviewed self result stays out
     * until somebody looks at it.
     *
     * record_mode is pinned to 'player' as well. An unattributed time or a
     * typed team average describes a TEAM, and would be a different child's
     * number if it ever landed in one. measuredRawByPlayer() already drops
     * them for want of a player_id; this makes the intent explicit at the
     * query rather than leaving it to a filter two modules away.
     */
    baselineResultsForSeason: (seasonId) =>
      requestAll(
        'baseline_results?select=player_id,exercise_key,value,baseline_sessions!inner(season_id)' +
        `&baseline_sessions.season_id=eq.${encodeURIComponent(seasonId)}` +
        '&record_mode=eq.player&player_id=not.is.null' +
        '&or=(source.is.null,source.neq.self,accepted_at.not.is.null)' +
        '&order=player_id'
      ),

    /**
     * Every TEAM-level result of a season (record_mode anon or average), for
     * the team + age-group averages. Deliberately a separate call from the
     * per-player one above: these rows must never be mistaken for a child's.
     */
    teamResultsForSeason: (seasonId) =>
      requestAll(
        'baseline_results?select=team_id,exercise_key,value,subject_count,record_mode,recorded_at' +
        ',baseline_sessions!inner(season_id)' +
        `&baseline_sessions.season_id=eq.${encodeURIComponent(seasonId)}` +
        '&record_mode=in.(anon,average)&order=team_id'
      ),

    // Most recent snapshot time for a season (throttles the auto-snapshot).
    lastSnapshotAt: async (seasonId) => {
      const rows = await request(
        'dragons_score_snapshots?select=computed_at' +
        `&season_id=eq.${encodeURIComponent(seasonId)}&order=computed_at.desc&limit=1`
      );
      return rows && rows[0] ? rows[0].computed_at : null;
    },

    // Team anchors ("shelf heights") for the tier-anchored coach axis -
    // director calibration values, per season.
    anchorsForSeason: (seasonId) =>
      request(`team_anchors?select=team_id,anchor&season_id=eq.${encodeURIComponent(seasonId)}`),

    saveAnchors: (rows) =>
      rows.length
        ? request('team_anchors?on_conflict=team_id,season_id', {
            method: 'POST',
            prefer: 'resolution=merge-duplicates,return=minimal',
            body: JSON.stringify(rows),
          })
        : Promise.resolve(),

    deleteAnchor: (teamId, seasonId) =>
      request(
        `team_anchors?team_id=eq.${encodeURIComponent(teamId)}&season_id=eq.${encodeURIComponent(seasonId)}`,
        { method: 'DELETE', prefer: 'return=minimal' }
      ),

    // Append a batch of score snapshots (one per ranked player).
    writeSnapshots: (rows) =>
      rows.length
        ? request('dragons_score_snapshots', {
            method: 'POST', prefer: 'return=minimal', body: JSON.stringify(rows),
          })
        : Promise.resolve(),
  };

  // ── POST-GAME NOMINATIONS (coach top-3 / bottom-2) ──────────────────────────
  // RLS makes coach reads blind (own picks only); director reads all.

  // ── LINE-UP MANAGER (coach page; participation lens, not a score input) ─────
  const lineups = {
    // Match line-ups for a team + season (newest first). RLS returns the
    // director's or the coach's own assigned-team rows.
    listForTeam: (teamId, seasonId) =>
      request(
        `lineups?team_id=eq.${encodeURIComponent(teamId)}` +
        `&season_id=eq.${encodeURIComponent(seasonId)}&select=*&order=match_date.desc`
      ),

    getSpots: (lineupId) =>
      request(`lineup_spots?lineup_id=eq.${encodeURIComponent(lineupId)}&select=*`),

    create: async (data) =>
      (await request('lineups', { method: 'POST', body: JSON.stringify(data) }))?.[0] || null,

    update: (id, data) =>
      request(`lineups?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal', body: JSON.stringify(data),
      }),

    remove: (id) =>
      request(`lineups?id=eq.${encodeURIComponent(id)}`, { method: 'DELETE', prefer: 'return=minimal' }),

    // Replace the whole spot set for one line-up (delete-then-insert).
    saveSpots: async (lineupId, spots) => {
      await request(`lineup_spots?lineup_id=eq.${encodeURIComponent(lineupId)}`,
        { method: 'DELETE', prefer: 'return=minimal' });
      if (spots.length) {
        await request('lineup_spots', {
          method: 'POST', prefer: 'return=minimal',
          body: JSON.stringify(spots.map(s => ({ ...s, lineup_id: lineupId }))),
        });
      }
    },

    // All spots for a team+season - feeds the participation rollup. Embeds the
    // parent line-up so a spot without a matching (team,season) is excluded.
    participationForTeam: (teamId, seasonId) =>
      request(
        'lineup_spots?select=player_id,status,line,position,lineups!inner(team_id,season_id)' +
        `&lineups.team_id=eq.${encodeURIComponent(teamId)}` +
        `&lineups.season_id=eq.${encodeURIComponent(seasonId)}`
      ),
  };

  // ── POST-MATCH SELF-REFLECTION (coach page + player page) ───────────────────
  // A coach opens a reflection for one match; players fill an age-banded
  // questionnaire; the coach comments or marks it seen. STANDALONE lens - never
  // a Dragons Score input. RLS: director all; assigned staff read submissions +
  // write reviews for their teams; player owns own answers.
  const reflections = {
    // Coach/director: reflections for a team + season, newest first, each with a
    // count of how many players have responded so far.
    requestsForTeam: (teamId, seasonId) =>
      request(
        `self_eval_requests?team_id=eq.${encodeURIComponent(teamId)}` +
        `&season_id=eq.${encodeURIComponent(seasonId)}` +
        '&select=*,self_evals(count)&order=match_date.desc,created_at.desc'
      ),

    createRequest: async (data) =>
      (await request('self_eval_requests', { method: 'POST', body: JSON.stringify(data) }))?.[0] || null,

    // Stop (or reopen) accepting responses.
    setClosed: (id, closed) =>
      request(`self_eval_requests?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ closed_at: closed ? new Date().toISOString() : null }),
      }),

    deleteRequest: (id) =>
      request(`self_eval_requests?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    // Coach/director: every player's reflection for one request, with the
    // player's name and the coach's existing review embedded.
    submissionsForRequest: (requestId) =>
      request(
        `self_evals?request_id=eq.${encodeURIComponent(requestId)}` +
        '&select=*,players(name),self_eval_reviews(*)&order=submitted_at'
      ),

    // Coach: leave (or update) feedback on one reflection. comment null = the
    // coach only marks it seen. Upsert keyed on the reflection.
    saveReview: (selfEvalId, coachStaffId, comment) =>
      request('self_eval_reviews?on_conflict=self_eval_id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({
          self_eval_id: selfEvalId,
          coach_staff_id: coachStaffId || null,
          comment: (comment && comment.trim()) ? comment.trim() : null,
          reviewed_at: new Date().toISOString(),
        }),
      }),
  };

  // ── FREE-TEXT TRANSLATIONS (Phase 2 - coach feedback EN<->NL) ────────────────
  // The AI call rides the `translate` Edge Function (OpenAI gpt-4o); the stored
  // rows keep the original + translation so the player sees both. Score-neutral.
  const translations = {
    // Call the AI translate endpoint. Returns translated text, or throws.
    ai: async (text, target) => {
      const { url, anonKey } = CONFIG.supabase;
      const token = (AUTH.getToken && AUTH.getToken()) || anonKey;
      const res = await fetch(`${url}/functions/v1/translate`, {
        method: 'POST',
        headers: { apikey: anonKey, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, target }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.translation) throw new Error(body.error || 'translate_failed');
      return body.translation;
    },

    // Director/coach: stored translations for an evaluation (or a set of them).
    forEvaluation: (evalId) =>
      request(`evaluation_translations?evaluation_id=eq.${encodeURIComponent(evalId)}&select=*`),

    forEvaluations: (ids) =>
      ids.length
        ? request(`evaluation_translations?evaluation_id=in.(${ids.map(encodeURIComponent).join(',')})&select=*`)
        : Promise.resolve([]),

    // Upsert one (evaluation, field, language) translation. source_text is the
    // original at save time so a later feedback edit can be detected as stale.
    save: (row) =>
      request('evaluation_translations?on_conflict=evaluation_id,field,target_lang', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({ ...row, updated_at: new Date().toISOString() }),
      }),

    remove: (evalId, field, targetLang) =>
      request(
        `evaluation_translations?evaluation_id=eq.${encodeURIComponent(evalId)}` +
        `&field=eq.${encodeURIComponent(field)}&target_lang=eq.${encodeURIComponent(targetLang)}`,
        { method: 'DELETE', prefer: 'return=minimal' }
      ),
  };

  const gameNominations = {
    // A coach's own picks for a team (blind to other coaches via RLS).
    listMine: (teamId) =>
      request(`game_nominations?team_id=eq.${encodeURIComponent(teamId)}&select=*&order=game_date.desc`),

    // Replace this coach's picks for one game (team + date): delete own rows for
    // that date (RLS scopes the delete to the caller), then insert the new set.
    replaceGame: async (teamId, gameDate, rows) => {
      await request(
        `game_nominations?team_id=eq.${encodeURIComponent(teamId)}&game_date=eq.${encodeURIComponent(gameDate)}`,
        { method: 'DELETE', prefer: 'return=minimal' }
      );
      if (rows.length) {
        await request('game_nominations', {
          method: 'POST', prefer: 'return=minimal', body: JSON.stringify(rows),
        });
      }
    },

    // Director-side reads (for tracking + future bias analysis).
    listForPlayer: (playerId) =>
      request(`game_nominations?player_id=eq.${encodeURIComponent(playerId)}&select=*`),

    listForSeason: (seasonId) =>
      request(`game_nominations?season_id=eq.${encodeURIComponent(seasonId)}&select=*`),
  };

  // ── LONG-TERM TRACKING (Module 4 - director-only) ───────────────────────────
  // All reads cross seasons; RLS already restricts these tables to the director.

  const tracking = {
    // Player picker source: every player x season summary row (deduped client-
    // side to one entry per player). Light columns only.
    pickerRows: () =>
      request('player_season_summary?select=player_id,player_name,season_id,age_group,team_id,line&order=player_name'),

    // Full cross-season summary for one player (the history screen's spine).
    summaryForPlayer: (playerId) =>
      request(
        'player_season_summary?select=*' +
        `&player_id=eq.${encodeURIComponent(playerId)}&order=season_id`
      ),

    // Dragons Score history for one player (append-only; ordered oldest first).
    snapshotsForPlayer: (playerId) =>
      request(
        'dragons_score_snapshots?select=season_id,scope,score,axis_coach,axis_selection,axis_measured,confidence,computed_at' +
        `&player_id=eq.${encodeURIComponent(playerId)}&order=computed_at`
      ),

    // Post-game nominations for one player (top/bottom across all games).
    nominationsForPlayer: (playerId) =>
      request(`game_nominations?player_id=eq.${encodeURIComponent(playerId)}&select=*`),
  };

  // ── DIRECTOR PLAYER NOTES & FLAGS (Module 4 add-on, director-only) ──────────

  const directorNotes = {
    // Current flag/tags/potential for one player (or null).
    getAssessment: async (playerId) => {
      const rows = await request(`player_director_assessment?player_id=eq.${encodeURIComponent(playerId)}&select=*`);
      return rows && rows[0] ? rows[0] : null;
    },

    // Upsert the assessment (one row per player).
    saveAssessment: (playerId, patch) =>
      request('player_director_assessment?on_conflict=player_id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=minimal',
        body: JSON.stringify({ player_id: playerId, ...patch, updated_at: new Date().toISOString() }),
      }),

    listNotes: (playerId) =>
      request(`player_tracking_notes?player_id=eq.${encodeURIComponent(playerId)}&select=*&order=note_date.desc,created_at.desc`),

    addNote: (note) =>
      request('player_tracking_notes', { method: 'POST', prefer: 'return=minimal', body: JSON.stringify(note) }),

    deleteNote: (id) =>
      request(`player_tracking_notes?id=eq.${encodeURIComponent(id)}`, { method: 'DELETE', prefer: 'return=minimal' }),

    listMarks: (playerId) =>
      request(`player_tracking_marks?player_id=eq.${encodeURIComponent(playerId)}&select=*&order=mark_date.desc`),

    addMark: (playerId, kind, markDate) =>
      request('player_tracking_marks', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify({ player_id: playerId, kind, ...(markDate ? { mark_date: markDate } : {}) }),
      }),
  };

  // ── PUBLIC API ───────────────────────────────────────────────────────────────


  // ── Sharing settings ────────────────────────────────────────────────────────
  // How much of a published evaluation a player sees. Rows exist only where
  // someone has overridden something, so the club row is the real default and
  // everything else is an exception (see share-settings.js for the resolver).
  const shareSettings = {
    list: (seasonId) =>
      request(`share_settings?season_id=eq.${encodeURIComponent(seasonId)}&select=*`),

    /**
     * Write one scope's settings. Upserts on the scope, so repeatedly saving
     * the club row updates it rather than piling up duplicates; the partial
     * unique indexes in the migration are what make that safe.
     */
    save: (seasonId, scope, scopeKey, fields) =>
      // One conflict target for every scope, and it must start with the club.
      //
      // Two bugs met here. It used to send just `season_id` for the club, to
      // match a PARTIAL unique index, and Postgres refuses to use a partial
      // index for an upsert unless the statement repeats the index's WHERE
      // clause, which PostgREST cannot send: every save failed with 42P10 from
      // the day the screen shipped. And once the table gained a club, leaving
      // it out of the key meant a second club's row collided with Dragons' and
      // the upsert became an attempt to overwrite it, which the policies
      // refused. club_id is not in the body; the column default fills it from
      // the caller's own token, and naming it here is what keeps two clubs
      // apart.
      request('share_settings?on_conflict=club_id,season_id,scope,scope_key', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=representation',
        body: JSON.stringify({
          season_id: seasonId,
          scope,
          scope_key: scope === 'club' ? null : String(scopeKey),
          ...fields,
          updated_at: new Date().toISOString(),
        }),
      }),

    /** Drop an override so this scope inherits again. Never used on 'club'. */
    clear: (seasonId, scope, scopeKey) =>
      request(`share_settings?season_id=eq.${encodeURIComponent(seasonId)}` +
              `&scope=eq.${encodeURIComponent(scope)}` +
              `&scope_key=eq.${encodeURIComponent(scopeKey)}`,
              { method: 'DELETE', prefer: 'return=minimal' }),
  };


  // ── Team files: the season schedule and match briefings ────────────────────
  // Uploaded documents in the private 'team-files' bucket. The object path
  // always starts with the team id, because that is what the storage policy
  // matches on to decide who may download it.
  const TEAM_FILES_BUCKET = 'team-files';

  const teamFiles = {
    /** Everything for a team. RLS gives a squad only the published rows. */
    listForTeam: (teamId) =>
      request(`team_files?team_id=eq.${encodeURIComponent(teamId)}` +
              '&select=*&order=kind.asc,match_date.desc.nullslast,uploaded_at.desc'),

    create: (row) =>
      request('team_files', {
        method: 'POST', prefer: 'return=representation',
        body: JSON.stringify(row),
      }),

    setPublished: (id, published) =>
      request(`team_files?id=eq.${encodeURIComponent(id)}`, {
        method: 'PATCH', prefer: 'return=minimal',
        body: JSON.stringify({ published_at: published ? new Date().toISOString() : null }),
      }),

    remove: (id) =>
      request(`team_files?id=eq.${encodeURIComponent(id)}`, {
        method: 'DELETE', prefer: 'return=minimal',
      }),

    upload: async (path, file) => {
      await _storageRequest(path, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
        body: file,
      }, TEAM_FILES_BUCKET);
      return path;
    },

    /**
     * A blob URL the browser can open. The bucket is private, so there is no
     * shareable link to hand out: the file is fetched with the session's own
     * token and held in memory for as long as the tab needs it.
     */
    openUrl: async (path) => {
      const r = await _storageRequest(path, { method: 'GET' }, TEAM_FILES_BUCKET);
      return URL.createObjectURL(await r.blob());
    },

    removeFile: (path) =>
      _storageRequest(path, { method: 'DELETE' }, TEAM_FILES_BUCKET),
  };

  // ── Squad page (one shared code, players pick their own name) ───────────────
  // A squad session can WRITE through the function below and can read none of
  // these tables, so that "only the coach sees this" is true in the database
  // and not just on the screen. The director's reads further down use their
  // own policies.
  const squad = {
    /**
     * The ONLY way a squad session writes. Reflection, teammate note and coach
     * feedback go in one call to a SECURITY DEFINER function, because squad
     * sessions have no direct access to those tables: the form promises "only
     * the coach sees this", and table-level grants could not keep that promise
     * when a shared code means the database cannot tell one player from
     * another. The function takes the team from the token, not from here.
     */
    submitReflection: (args) =>
      request('rpc/submit_match_reflection', {
        method: 'POST', prefer: 'return=minimal',
        body: JSON.stringify(args),
      }),

    // ── The director's side. These are reads, under the director policies,
    // and were lost once when the write half of this block was replaced:
    // squad-replies.js kept calling them and its .catch(() => []) turned the
    // crash into a blank screen rather than an error anyone could see.
    /**
     * Player names come through the join rather than a separate lookup,
     * because players.getAll is scoped to the club being worn and a Dragons
     * director reading Victory's replies would otherwise get an empty name on
     * every row.
     */
    reflectionsForTeam: (teamId) =>
      request(`match_reflections?team_id=eq.${encodeURIComponent(teamId)}` +
              '&select=*,players(id,name)&order=match_date.desc,submitted_at.asc'),

    positivesForTeam: (teamId) =>
      request(`teammate_positives?team_id=eq.${encodeURIComponent(teamId)}` +
              '&select=*,from_player:from_player_id(id,name),about_player:about_player_id(id,name)' +
              '&order=match_date.desc'),

    /** Who is in the squad, for showing who has not replied yet. */
    rosterForTeam: (teamId, seasonId) =>
      request(`memberships?team_id=eq.${encodeURIComponent(teamId)}` +
              `&season_id=eq.${encodeURIComponent(seasonId)}&select=players(id,name)`),

    /** Anonymous: there is no player to join to, and that is the point. */
    coachFeedbackForTeam: (teamId) =>
      request(`coach_feedback?team_id=eq.${encodeURIComponent(teamId)}` +
              '&select=*&order=match_date.desc,created_at.asc'),

  };

  return {
    health,
    seasons,
    teams,
    players,
    memberships,
    teamChanges,
    codes,
    evaluations,
    ceilings,
    selection,
    evaluationPlans,
    staff,
    playerSeasonSummary,
    playerPage,
    playerAccess,
    shareSettings,
    gradingPasses,
    rubric,
    squad,
    teamFiles,
    staffAccess,
    staffHub,
    dta,
    exercises,
    practices,
    baseline,
    physicalTests,
    ranking,
    tracking,
    directorNotes,
    lineups,
    reflections,
    translations,
    gameNominations,
  };

})();
